/**
 * Cart Drawer — Hyper-style
 */
(function () {
  'use strict';

  if (window.__cartDrawerInitialized) return;
  window.__cartDrawerInitialized = true;

  const CartGoals = Object.freeze({
    tierPercentages(labels) {
      return (labels || [])
        .map((label) => {
          const match = String(label || '').match(/(\d+(?:\.\d+)?)\s*%/);
          return match ? Number(match[1]) : null;
        })
        .filter((value) => Number.isFinite(value));
    },

    presentmentThresholds(thresholds, rate) {
      const safeRate = Number.isFinite(Number(rate)) && Number(rate) > 0 ? Number(rate) : 1;
      return (thresholds || []).map((threshold) => Math.round((Number(threshold) || 0) * safeRate));
    },

    milestonesFromElement(element) {
      return Array.from(element?.querySelectorAll('[data-milestone]') || [])
        .map((node, index) => {
          const slot = parseInt(node.dataset.milestone, 10) || index + 1;
          const threshold = parseInt(
            node.dataset.threshold || element?.getAttribute(`data-threshold-${slot}`),
            10,
          ) || 0;
          const label = String(
            node.dataset.label || element?.getAttribute(`data-label-${slot}`) || '',
          ).trim();
          const basis = node.dataset.basis === 'shipping' || slot === 1 ? 'shipping' : 'eligible';
          return { node, slot, threshold, label, basis };
        })
        .filter(({ threshold, label }) => threshold > 0 && label);
    },

    milestoneState({ milestones, currencyRate, shippingTotal, tierEligibleTotal }) {
      const safeRate = Number.isFinite(Number(currencyRate)) && Number(currencyRate) > 0
        ? Number(currencyRate)
        : 1;
      const tiers = (milestones || []).map((milestone) => ({
        ...milestone,
        threshold: Math.round((Number(milestone.threshold) || 0) * safeRate),
      }));
      const shipping = Number(shippingTotal) || 0;
      const eligible = Number(tierEligibleTotal) || 0;
      const totalFor = (tier) => tier.basis === 'shipping' ? shipping : eligible;
      const unlocked = tiers.map((tier) => totalFor(tier) >= tier.threshold);
      const nextIndex = unlocked.findIndex((value) => !value);
      let fillPercent = tiers.length && nextIndex === -1 ? 100 : 0;

      if (nextIndex >= 0) {
        const tier = tiers[nextIndex];
        const segmentSize = 100 / tiers.length;
        const previousTier = tiers[nextIndex - 1];
        const segmentStartThreshold = previousTier?.basis === tier.basis
          ? previousTier.threshold
          : 0;
        const segmentRange = tier.threshold - segmentStartThreshold;
        const segmentProgress = Math.max(0, totalFor(tier) - segmentStartThreshold);
        const ratio = segmentRange > 0
          ? Math.max(0, Math.min(1, segmentProgress / segmentRange))
          : 0;
        fillPercent = (nextIndex * segmentSize) + (ratio * segmentSize);
      }

      fillPercent = Math.max(0, Math.min(100, fillPercent));
      const remaining = nextIndex === -1
        ? 0
        : Math.max(0, tiers[nextIndex].threshold - totalFor(tiers[nextIndex]));

      return { milestones: tiers, unlocked, fillPercent, nextIndex, remaining };
    },

    eligibleSubtotal(cart, tierRates) {
      const rates = new Set((tierRates || []).map((rate) => Number(rate)));
      const allocationsForItem = (item) => [
        ...(item?.line_level_discount_allocations || []),
        ...(item?.discounts || []),
      ];
      const isTierAllocation = (allocation) => {
        const application = allocation?.discount_application || allocation || {};
        const valueType = String(application.value_type || '').toLowerCase();
        const titleMatch = String(application.title || '').match(/(\d+(?:\.\d+)?)\s*%/);
        const rate = valueType === 'percentage' && Number.isFinite(Number(application.value))
          ? Number(application.value)
          : titleMatch ? Number(titleMatch[1]) : null;
        return rate !== null && rates.has(rate);
      };

      let subtotal = 0;
      (cart?.items || []).forEach((item) => {
        const allocations = allocationsForItem(item);
        const hasTierAllocation = allocations.some(isTierAllocation);
        const hasPriceReduction = Number(item?.final_line_price) < Number(item?.original_line_price);
        const hasOtherLineDiscount = allocations.some((allocation) => !isTierAllocation(allocation)) ||
          (hasPriceReduction && !hasTierAllocation);

        // A line exclusively claimed by another product discount cannot advance
        // the tier reward. If Shopify is already combining a tier on the line,
        // its full pre-tier value remains eligible.
        if (hasOtherLineDiscount && !hasTierAllocation) return;

        subtotal += Number(
          item?.original_line_price ?? item?.line_price ?? item?.final_line_price ?? 0,
        ) || 0;
      });
      return subtotal;
    },

    shippingSubtotal(cart) {
      return Number(cart?.items_subtotal_price ?? cart?.total_price) || 0;
    },

    hasFlashDeal(cart) {
      return (cart?.items || []).some((item) => {
        const allocations = [
          ...(item?.line_level_discount_allocations || []),
          ...(item?.discounts || []),
        ];
        return allocations.some((allocation) => {
          const application = allocation?.discount_application || allocation || {};
          const title = String(application.title || '').trim().toUpperCase();
          return title.startsWith('CLEARANCE') || title.startsWith('FINAL SALE') || title.startsWith('FLASHDEAL');
        });
      });
    },

    shippingNote(baseText, exclusionText) {
      const base = String(baseText || '').trim().replace(/[.\s]+$/, '');
      const exclusion = String(exclusionText || '').trim();
      return exclusion ? `${base}. ${exclusion}` : `${base}.`;
    },
  });
  window.HealixCartGoals = CartGoals;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCartDrawer);
  } else {
    initCartDrawer();
  }

  const MILESTONE_CHECK_SVG =
    '<svg class="cart-drawer__milestone-check" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>';

  function initCartDrawer() {
    const drawer = document.querySelector('[data-cart-drawer]');
    if (!drawer) return;

    const configEl = document.querySelector('[data-cart-drawer-config]');
    let config = { openOnAdd: true, sectionId: drawer.dataset.sectionId };
    if (configEl) {
      try {
        config = { ...config, ...JSON.parse(configEl.textContent) };
      } catch (e) {
        /* ignore */
      }
    }

    const openOnAdd = drawer.dataset.openOnAdd !== 'false' && config.openOnAdd !== false;
    const sectionId = config.sectionId || drawer.dataset.sectionId;

    let notesSaveTimeout = null;
    const removingKeys = new Set();
    let sectionRenderToken = 0;

    function setDrawerUpdating(isUpdating) {
      drawer.classList.toggle('is-updating', isUpdating);
      const content = getContent();
      if (content) content.setAttribute('aria-busy', isUpdating ? 'true' : 'false');
    }

    async function refreshDrawerFromCart() {
      const token = ++sectionRenderToken;
      setDrawerUpdating(true);
      try {
        await renderSection();
      } finally {
        if (token === sectionRenderToken) setDrawerUpdating(false);
      }
    }

    function getContent() {
      return drawer.querySelector('.cart-drawer__content');
    }

    function hasGsap() {
      return typeof gsap !== 'undefined';
    }

    function clearDrawerShellGsap() {
      const content = getContent();
      const overlay = drawer.querySelector('[data-cart-overlay]');
      if (hasGsap()) {
        if (content) {
          gsap.killTweensOf(content);
          gsap.set(content, { clearProps: 'transform' });
        }
        if (overlay) {
          gsap.killTweensOf(overlay);
          gsap.set(overlay, { clearProps: 'opacity' });
        }
      } else {
        if (content) content.style.transform = '';
        if (overlay) overlay.style.opacity = '';
      }
    }

    function initGsapLayout() {
      // Sheets are positioned/animated by ui-sheet (CSS); nothing to preset here.
    }

    function refreshRefs() {
      return {
        closeBtn: drawer.querySelector('[data-cart-close]'),
        overlay: drawer.querySelector('[data-cart-overlay]'),
        filled: drawer.querySelector('[data-cart-drawer-filled]'),
        emptyWrap: drawer.querySelector('[data-cart-drawer-empty-wrap]'),
        itemsContainer: drawer.querySelector('[data-cart-drawer-items]'),
        countEl: drawer.querySelector('[data-cart-drawer-count]'),
        footer: drawer.querySelector('[data-cart-drawer-footer]'),
        footerEmpty: drawer.querySelector('[data-cart-footer-empty]'),
        footerFilled: drawer.querySelector('[data-cart-footer-filled]'),
        pills: drawer.querySelector('[data-cart-drawer-pills]'),
        milestones: drawer.querySelector('[data-cart-drawer-milestones]'),
        notesTextarea: drawer.querySelector('[data-cart-note]'),
      };
    }

    function openDrawer() {
      if (drawer.getAttribute('aria-hidden') === 'false') return;

      closeActiveSheet(true);
      clearDrawerShellGsap();
      updateMilestonesFromMarkup();

      drawer.setAttribute('aria-hidden', 'false');
      document.body.classList.add('cart-drawer-open');
      document.querySelectorAll('[data-cart-toggle]').forEach((btn) => {
        btn.setAttribute('aria-expanded', 'true');
      });

      requestAnimationFrame(() => {
        animateDrawerOpen();
        if (window.UntamedIcons?.refresh) window.UntamedIcons.refresh();
      });

      window.dispatchEvent(new CustomEvent('cart:opened'));
      updateHeaderCountFromCart();
      loadRecommendations();
    }

    function closeDrawer() {
      if (drawer.getAttribute('aria-hidden') === 'true') return;

      drawer.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('cart-drawer-open');
      document.querySelectorAll('[data-cart-toggle]').forEach((btn) => {
        btn.setAttribute('aria-expanded', 'false');
      });

      const content = getContent();
      const onClosed = () => {
        clearDrawerShellGsap();
        closeActiveSheet(true);
        if (hasGsap()) initGsapLayout();
        window.dispatchEvent(new CustomEvent('cart:closed'));
      };

      if (content) {
        content.addEventListener('transitionend', (e) => {
          if (e.propertyName === 'transform') onClosed();
        }, { once: true });
      } else {
        onClosed();
      }
    }

    function toggleDrawer() {
      if (drawer.getAttribute('aria-hidden') === 'false') closeDrawer();
      else openDrawer();
    }

    function animateDrawerOpen() {
      const refs = refreshRefs();
      if (!hasGsap()) return;

      if (refs.milestones) {
        const fill = refs.milestones.querySelector('[data-milestones-fill]');
        if (fill) {
          const target = fill.style.width || window.getComputedStyle(fill).width || '0%';
          fill.style.width = '0%';
          gsap.to(fill, { width: target, duration: 1, ease: 'power2.out', delay: 0.2 });
        }
      }

      if (refs.filled && !refs.filled.hidden) {
        const items = refs.filled.querySelectorAll('[data-cart-drawer-item]');
        const upsells = refs.filled.querySelectorAll('.cart-upsell-card');
        if (items.length) {
          gsap.fromTo(
            items,
            { opacity: 0, y: 12 },
            { opacity: 1, y: 0, duration: 0.4, stagger: 0.06, ease: 'power3.out', delay: 0.15 }
          );
        }
        if (upsells.length) {
          gsap.fromTo(
            upsells,
            { opacity: 0, x: 16 },
            { opacity: 1, x: 0, duration: 0.35, stagger: 0.05, ease: 'power2.out', delay: 0.25 }
          );
        }
      } else if (refs.emptyWrap && !refs.emptyWrap.hidden) {
        const icon = drawer.querySelector('[data-empty-cart-icon]');
        const navItems = drawer.querySelectorAll('[data-empty-nav-item]');
        const perks = drawer.querySelectorAll('[data-empty-perk-item]');
        if (icon) {
          gsap.fromTo(
            icon,
            { scale: 0.85, opacity: 0 },
            { scale: 1, opacity: 1, duration: 0.5, ease: 'back.out(1.5)' }
          );
        }
        if (navItems.length) {
          gsap.fromTo(
            navItems,
            { opacity: 0, x: -10 },
            { opacity: 1, x: 0, duration: 0.3, stagger: 0.05, ease: 'power2.out', delay: 0.1 }
          );
        }
        if (perks.length) {
          gsap.fromTo(
            perks,
            { opacity: 0, y: 10 },
            { opacity: 1, y: 0, duration: 0.35, stagger: 0.08, ease: 'power2.out', delay: 0.15 }
          );
        }
      }
    }

    // Sheets (note / shipping / coupon / upsell) are driven by the shared
    // ui-sheet component (ui-sheet.js). closeActiveSheet lets the drawer
    // open/close reset also dismiss any sheet layered over it.
    function closeActiveSheet() {
      if (window.UISheet) window.UISheet.close();
    }

    function getCartRoot() {
      const root = window.Shopify?.routes?.root || '/';
      return root.endsWith('/') ? root : `${root}/`;
    }

    function discountWasApplied(cart) {
      return (
        (cart.cart_level_discount_applications || []).length > 0 || Number(cart.total_discount || 0) > 0
      );
    }

    function setCouponLoading(isLoading) {
      drawer.querySelectorAll('[data-coupon-apply], [data-coupon-apply-code]').forEach((btn) => {
        btn.disabled = isLoading;
        if (isLoading) btn.setAttribute('aria-busy', 'true');
        else btn.removeAttribute('aria-busy');
      });
    }

    function collectDiscountErrorText(data) {
      const chunks = [];
      if (data?.description) chunks.push(String(data.description));
      if (data?.message && data.message !== 'Cart Error') chunks.push(String(data.message));
      if (Array.isArray(data?.errors)) {
        data.errors.forEach((err) => chunks.push(String(err)));
      } else if (data?.errors && typeof data.errors === 'object') {
        Object.values(data.errors).forEach((val) => {
          if (Array.isArray(val)) val.forEach((item) => chunks.push(String(item)));
          else chunks.push(String(val));
        });
      }
      return chunks.join(' ').toLowerCase();
    }

    function parseDiscountError(res, data, code) {
      const text = collectDiscountErrorText(data);

      if (text.includes('expir')) {
        return 'This discount code has expired.';
      }
      if (
        text.includes('not valid') ||
        text.includes('invalid') ||
        text.includes("isn't valid") ||
        text.includes('is not valid') ||
        text.includes('does not exist') ||
        text.includes("doesn't exist") ||
        text.includes('not found') ||
        text.includes('unable to find') ||
        text.includes('no valid discount') ||
        text.includes('unknown discount')
      ) {
        return 'This discount code is invalid or does not exist.';
      }
      if (text.includes('already been applied') || text.includes('already applied')) {
        return 'This discount code is already applied to your cart.';
      }
      if (
        text.includes('not applicable') ||
        text.includes("doesn't apply") ||
        text.includes('does not apply') ||
        text.includes('requirements') ||
        text.includes('minimum') ||
        text.includes('eligible')
      ) {
        return (
          data?.description ||
          data?.message ||
          "This discount code doesn't apply to your cart."
        );
      }

      if (data?.description) return data.description;
      if (data?.message && data.message !== 'Cart Error') return data.message;

      if (res && !res.ok) {
        return `We couldn't apply "${code}". Check the code and try again.`;
      }

      return 'This discount code could not be applied. It may be expired, invalid, or not eligible for your cart.';
    }

    function showCouponError(message) {
      const errorEl = drawer.querySelector('[data-coupon-error]');
      const input = drawer.querySelector('[data-coupon-input]');
      const form = drawer.querySelector('.cart-drawer__coupon-form');
      if (errorEl) {
        errorEl.hidden = false;
        errorEl.textContent = message;
      }
      if (form) form.classList.add('is-error');
      if (input) {
        input.setAttribute('aria-invalid', 'true');
        input.focus();
      }
    }

    function clearCouponError() {
      const errorEl = drawer.querySelector('[data-coupon-error]');
      const input = drawer.querySelector('[data-coupon-input]');
      const form = drawer.querySelector('.cart-drawer__coupon-form');
      if (errorEl) {
        errorEl.hidden = true;
        errorEl.textContent = '';
      }
      if (form) form.classList.remove('is-error');
      if (input) input.removeAttribute('aria-invalid');
    }

    async function saveNote(note) {
      try {
        await fetch(`${getCartRoot()}cart/update.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ note }),
        });
      } catch (e) {
        console.error('Failed to save note:', e);
      }
    }

    async function applyDiscountCode(code) {
      const trimmed = (code || '').trim();
      clearCouponError();

      if (!trimmed) {
        showCouponError('Please enter a discount code.');
        return false;
      }

      if (window.CartAPI?.beginChange) window.CartAPI.beginChange({ action: 'discount' });
      else window.dispatchEvent(new CustomEvent('cart:changing', { detail: { action: 'discount' } }));

      setCouponLoading(true);

      try {
        const res = await fetch(`${getCartRoot()}cart/update.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ discount: trimmed }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          const message = parseDiscountError(res, data, trimmed);
          showCouponError(message);
          window.dispatchEvent(new CustomEvent('cart:error', { detail: { message } }));
          return false;
        }
        if (!discountWasApplied(data)) {
          const message = parseDiscountError(res, data, trimmed);
          showCouponError(message);
          window.dispatchEvent(new CustomEvent('cart:error', { detail: { message } }));
          return false;
        }
        clearCouponError();
        window.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: data } }));
        closeActiveSheet();
        return true;
      } catch (e) {
        const message = e.message || 'Could not apply code. Please try again.';
        showCouponError(message);
        window.dispatchEvent(new CustomEvent('cart:error', { detail: { message } }));
        return false;
      } finally {
        setCouponLoading(false);
        if (window.CartAPI?.endChange) window.CartAPI.endChange();
        else window.dispatchEvent(new CustomEvent('cart:idle'));
      }
    }

    function formatShippingPrice(price) {
      const cents = Math.round(parseFloat(price || 0) * 100);
      return formatMoney(cents);
    }

    async function calculateShippingRates() {
      const countryEl = drawer.querySelector('[data-shipping-country]');
      const zipEl = drawer.querySelector('[data-shipping-zip]');
      const resultEl = drawer.querySelector('[data-shipping-result]');
      const calcBtn = drawer.querySelector('[data-shipping-calculate]');
      if (!countryEl || !zipEl || !resultEl) return;

      const country = countryEl.value?.trim();
      const zip = zipEl.value?.trim();
      if (!country || !zip) {
        resultEl.hidden = false;
        resultEl.textContent = 'Please enter country and postal code.';
        return;
      }

      const params = new URLSearchParams({
        'shipping_address[country]': country,
        'shipping_address[zip]': zip,
      });

      const root = getCartRoot();
      const query = params.toString();

      if (calcBtn) {
        calcBtn.disabled = true;
        calcBtn.setAttribute('aria-busy', 'true');
      }
      resultEl.hidden = false;
      resultEl.textContent = 'Calculating…';

      try {
        const prepareRes = await fetch(`${root}cart/prepare_shipping_rates.json?${query}`, { method: 'POST' });
        if (!prepareRes.ok) throw new Error('Could not start shipping calculation');

        let rates = null;
        for (let attempt = 0; attempt < 10; attempt++) {
          if (attempt > 0) await new Promise((r) => setTimeout(r, 500));
          const res = await fetch(`${root}cart/async_shipping_rates.json?${query}`);
          if (!res.ok) continue;
          const data = await res.json();
          if (data?.shipping_rates != null) {
            rates = data.shipping_rates;
            break;
          }
        }

        if (!rates || rates.length === 0) {
          resultEl.textContent = 'No shipping rates found for this address.';
          return;
        }

        resultEl.innerHTML = rates
          .map((rate) => {
            const name = escapeHtml(rate.presentment_name || rate.name || 'Shipping');
            const price = formatShippingPrice(rate.price);
            return `<div>${name}: ${price}</div>`;
          })
          .join('');
      } catch (e) {
        console.error('Shipping rates failed:', e);
        resultEl.textContent = 'Unable to calculate shipping. Please try again.';
      } finally {
        if (calcBtn) {
          calcBtn.disabled = false;
          calcBtn.removeAttribute('aria-busy');
        }
      }
    }

    async function updateQuantity(key, newQuantity) {
      if (window.CartAPI) {
        await window.CartAPI.updateItem(key, newQuantity);
        return;
      }

      window.dispatchEvent(new CustomEvent('cart:changing', { detail: { action: 'updateItem', key } }));
      try {
        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: key, quantity: newQuantity }),
        });
        if (!response.ok) throw new Error('Failed to update cart');
        const cart = await response.json();
        window.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
      } finally {
        window.dispatchEvent(new CustomEvent('cart:idle'));
      }
    }
    
    

    async function removeItem(key) {
      if (drawer.classList.contains('is-updating')) return;
      if (removingKeys.has(key)) return;
      const itemEl = drawer.querySelector(`[data-line-key="${key}"]`);
      if (!itemEl || itemEl.classList.contains('is-removing')) return;

      removingKeys.add(key);
      itemEl.classList.add('is-removing');

      try {
        if (window.CartAPI) await window.CartAPI.removeItem(key);
        else await updateQuantity(key, 0);
      } catch (e) {
        console.error('Failed to remove item:', e);
        itemEl.classList.remove('is-removing');
      } finally {
        setTimeout(() => removingKeys.delete(key), 500);
      }
    }

    async function renderSection() {
      if (!sectionId) return refreshCartFallback();

      try {
        const root = window.Shopify?.routes?.root || '/';
        const url = `${root}?sections=${encodeURIComponent(sectionId)}`;
        const res = await fetch(url);
        const json = await res.json();
        const html = json[sectionId];
        if (!html) return refreshCartFallback();

        const tpl = document.createElement('div');
        tpl.innerHTML = html;
        const newDrawer = tpl.querySelector('[data-cart-drawer]');
        if (!newDrawer) return refreshCartFallback();

        const wasOpen = drawer.getAttribute('aria-hidden') === 'false';
        const newContent = newDrawer.querySelector('.cart-drawer__content');
        const content = getContent();
        if (newContent && content) {
          content.innerHTML = newContent.innerHTML;
        }
        updateMilestonesFromMarkup();
        drawer.dataset.openOnAdd = newDrawer.dataset.openOnAdd;
        attachItemListeners();
        hydrateLineColorBadges();
        bindSheetEvents();
        updateHeaderCountFromCart();
        loadRecommendations();

        if (wasOpen) {
          requestAnimationFrame(animateDrawerOpen);
        }

        if (window.UntamedIcons?.refresh) window.UntamedIcons.refresh();
      } catch (e) {
        console.error('Section render failed:', e);
        return refreshCartFallback();
      }
    }

    /* ---- Dynamic upsells: Shopify product recommendations, rendered as PCH
       cards by the cart-recommendations section and injected into the drawer ---- */
    function bindUpsellAddButtons() {
      drawer.querySelectorAll('[data-cart-upsell-add]').forEach((btn) => {
        btn.onclick = (e) => {
          e.preventDefault();
          if (drawer.classList.contains('is-updating')) return;
          quickAddUpsell(btn);
        };
      });
    }

    let recoToken = 0;
    async function loadRecommendations() {
      const mount = drawer.querySelector('[data-cart-recos]');
      if (!mount) return;
      const wrap = mount.closest('[data-cart-drawer-upsells]');
      const token = ++recoToken;
      try {
        const cart = await (await fetch(`${getCartRoot()}cart.js`)).json();
        if (token !== recoToken) return;
        if (!cart || !cart.items || cart.item_count === 0) {
          if (wrap) wrap.hidden = true;
          return;
        }
        // Anchor recommendations on the highest-value item in the cart.
        let base = cart.items[0];
        cart.items.forEach((it) => {
          if ((it.final_line_price || it.line_price || 0) > (base.final_line_price || base.line_price || 0)) base = it;
        });
        const cartProductIds = new Set(cart.items.map((it) => String(it.product_id)));
        const limit = parseInt(mount.getAttribute('data-reco-limit'), 10) || 6;
        const intent = mount.getAttribute('data-reco-intent') || 'related';
        const url = `${getCartRoot()}recommendations/products?section_id=cart-recommendations`
          + `&product_id=${base.product_id}&intent=${encodeURIComponent(intent)}&limit=${limit}`;
        const html = await (await fetch(url)).text();
        if (token !== recoToken) return;
        const tpl = document.createElement('div');
        tpl.innerHTML = html;
        const cards = Array.from(tpl.querySelectorAll('[data-cart-upsell-card]'))
          .filter((c) => !cartProductIds.has(String(c.getAttribute('data-product-id'))))
          .slice(0, limit);
        mount.querySelectorAll('[data-cart-upsell-card]').forEach((c) => c.remove());
        const spacer = mount.querySelector('.cart-drawer__upsells-spacer');
        cards.forEach((card) => { spacer ? mount.insertBefore(card, spacer) : mount.appendChild(card); });
        if (cards.length) {
          if (wrap) wrap.hidden = false;
          bindUpsellAddButtons();
          if (window.UntamedIcons?.refresh) window.UntamedIcons.refresh();
        } else if (wrap) {
          wrap.hidden = true;
        }
      } catch (e) {
        if (wrap) wrap.hidden = true;
      }
    }

    async function refreshCartFallback() {
      try {
        const res = await fetch('/cart.js');
        const cart = await res.json();
        updateHeaderCount(cart.item_count);
        setCartState(cart.item_count === 0);
        if (cart.item_count > 0 && refreshRefs().itemsContainer) {
          renderCartItems(cart.items, cart);
        }
        updateFooterTotals(cart);
      } catch (e) {
        console.error('Failed to refresh cart:', e);
      }
    }

    async function updateHeaderCountFromCart() {
      try {
        const res = await fetch('/cart.js');
        const cart = await res.json();
        updateHeaderCount(cart.item_count);
        updateShippingNote(cart);
      } catch (e) {
        /* ignore */
      }
    }

    function updateHeaderCount(count) {
      const refs = refreshRefs();
      if (refs.countEl) refs.countEl.textContent = count;
      const headerCartCount = document.querySelector('[data-cart-count]');
      if (headerCartCount) {
        headerCartCount.textContent = count;
        headerCartCount.classList.toggle('is-visible', count > 0);
      }
    }

    function setCartState(isEmpty) {
      const refs = refreshRefs();
      if (refs.filled) refs.filled.hidden = isEmpty;
      if (refs.emptyWrap) refs.emptyWrap.hidden = !isEmpty;
      if (refs.footerEmpty) refs.footerEmpty.hidden = !isEmpty;
      if (refs.footerFilled) refs.footerFilled.hidden = isEmpty;
      if (refs.pills) refs.pills.hidden = isEmpty;
    }

    function updateMilestonesFromMarkup() {
      const el = drawer.querySelector('[data-cart-drawer-milestones]');
      if (!el) return;

      const milestones = CartGoals.milestonesFromElement(el);
      if (!milestones.length) {
        el.hidden = true;
        return;
      }
      el.hidden = false;
      let currencyRate = 1;
      if (
        el.dataset.thresholdsBaseCurrency === 'true' &&
        window.Shopify?.currency?.rate
      ) {
        const parsedRate = Number(window.Shopify.currency.rate);
        if (Number.isFinite(parsedRate) && parsedRate > 0) currencyRate = parsedRate;
      }
      const state = CartGoals.milestoneState({
        milestones,
        currencyRate,
        shippingTotal: parseInt(el.dataset.shippingTotal, 10) || 0,
        tierEligibleTotal: parseInt(el.dataset.tierEligibleTotal, 10) || 0,
      });

      const fill = el.querySelector('[data-milestones-fill]');
      if (fill) fill.style.width = `${state.fillPercent}%`;

      state.milestones.forEach((milestone, i) => {
        const node = milestone.node;
        const isComplete = state.unlocked[i];
        node.classList.toggle('is-complete', isComplete);
        node.classList.toggle('is-next', i === state.nextIndex);
        const dot = node.querySelector('.cart-drawer__milestone-dot');
        if (dot) {
          const hasCheck = dot.querySelector('.cart-drawer__milestone-check');
          if (isComplete && !hasCheck) {
            dot.innerHTML = MILESTONE_CHECK_SVG;
          } else if (!isComplete && hasCheck) {
            dot.innerHTML = '';
          }
        }
      });

      const msg = el.querySelector('[data-milestones-message]');
      if (!msg) return;

      const progressText = el.dataset.progressText || '';
      const successText = el.dataset.successText || '';
      if (state.nextIndex === -1) {
        const goals = state.milestones
          .filter((_, i) => state.unlocked[i])
          .map(({ label }) => label)
          .join(' & ');
        msg.textContent = successText.replace('[goals]', goals);
        return;
      }

      const nextMilestone = state.milestones[state.nextIndex];
      const progressTemplate = nextMilestone.basis === 'shipping'
        ? progressText
        : el.dataset.eligibleProgressText || progressText;
      msg.textContent = progressTemplate
        .replace('[amount]', formatMoney(state.remaining))
        .replace('[goal]', nextMilestone.label);
    }

    function updateShippingNote(cart) {
      const note = drawer.querySelector('[data-cart-shipping-note]');
      if (!note) return;
      const baseText = note.dataset.baseText || '';
      const exclusionText = CartGoals.hasFlashDeal(cart) ? note.dataset.flashExclusion || '' : '';
      note.textContent = CartGoals.shippingNote(baseText, exclusionText);
    }

    // Cart-wide uniform discount consolidation (ported from live theme):
    // if one line-level discount (same title) applies to ALL items, it is
    // shown once in the footer instead of inside each line item. Returns
    // { title, total } or null. Skips discounts already represented in
    // cart-level applications (avoids a duplicate row).
    function getSharedLineDiscount(cart) {
      const items = cart.items || [];
      if (!items.length) return null;

      const cartLevelDiscounts = cart.cart_level_discount_applications || [];
      const firstAllocs = items[0].line_level_discount_allocations || [];

      for (const candidate of firstAllocs) {
        const candidateTitle =
          (candidate.discount_application && candidate.discount_application.title) || candidate.title;
        if (!candidateTitle) continue;

        let total = 0;
        let appliesToAll = true;
        for (const item of items) {
          const itemAllocs = item.line_level_discount_allocations || [];
          const match = itemAllocs.find((a) => {
            const t = (a.discount_application && a.discount_application.title) || a.title;
            return t === candidateTitle;
          });
          if (!match) {
            appliesToAll = false;
            break;
          }
          total += parseInt(match.amount, 10) || 0;
        }

        if (appliesToAll) {
          if (cartLevelDiscounts.some((d) => d.title === candidateTitle)) return null;
          return { title: candidateTitle, total };
        }
      }
      return null;
    }

    function updateFooterTotals(cart) {
      const subtotalEl = drawer.querySelector('[data-cart-drawer-subtotal]');
      const discountWrap = drawer.querySelector('[data-cart-footer-discount]');
      const totalLabel = drawer.querySelector('[data-cart-total-label]');
      const applications = cart.cart_level_discount_applications || [];
      const sharedLineDiscount = getSharedLineDiscount(cart);
      const hasDiscount =
        applications.length > 0 || !!sharedLineDiscount || cart.original_total_price > cart.total_price;

      if (discountWrap) {
        discountWrap.hidden = !hasDiscount;
        if (hasDiscount && (applications.length > 0 || sharedLineDiscount)) {
          let discountsHTML = applications
            .map(
              (discount) => `
            <div class="cart-drawer__footer-discount-row">
              <span class="cart-drawer__footer-discount-label">
                <span class="ph-bold ph-tag"></span>
                ${escapeHtml(discount.title)}
              </span>
              <span class="cart-drawer__footer-discount-amount">-${formatMoney(discount.total_allocated_amount)}</span>
            </div>`
            )
            .join('');
          if (sharedLineDiscount) {
            discountsHTML += `
            <div class="cart-drawer__footer-discount-row">
              <span class="cart-drawer__footer-discount-label">
                <span class="ph-bold ph-tag"></span>
                ${escapeHtml(sharedLineDiscount.title)}
              </span>
              <span class="cart-drawer__footer-discount-amount">-${formatMoney(sharedLineDiscount.total)}</span>
            </div>`;
          }
          discountWrap.innerHTML = discountsHTML;
          if (window.UntamedIcons?.refresh) window.UntamedIcons.refresh();
        }
      }
      if (totalLabel) totalLabel.textContent = hasDiscount ? 'Subtotal' : 'Estimated total';

      if (subtotalEl) {
        if (cart.original_total_price > cart.total_price) {
          subtotalEl.innerHTML = `<span class="cart-drawer__subtotal-current">${formatMoney(cart.total_price)}</span><span class="cart-drawer__subtotal-compare">${formatMoney(cart.original_total_price)}</span>`;
        } else {
          subtotalEl.textContent = formatMoney(cart.total_price);
        }
      }
      updateShippingNote(cart);
    }

    function buildDisplayTitle(item) {
      return item.product_title || item.title;
    }

    // Meta line ("swatch dot + Color · Size Size…") for the JS-rebuilt fallback
    // rows — mirrors the server markup in product-card-horizontal.liquid.
    function buildLineMeta(item) {
      var opts = item.options_with_values || [];
      var colorValue = '';
      var otherParts = [];
      opts.forEach(function (o) {
        if (!o || !o.value || o.value === 'Default Title') return;
        if (/colou?r/i.test(o.name || '')) {
          colorValue = String(o.value).trim();
        } else {
          otherParts.push(String(o.name).trim() + ' ' + String(o.value).trim());
        }
      });
      if (!colorValue && !otherParts.length) return '';

      var swatch = colorValue
        ? '<span class="cart-drawer__line-swatch" data-line-swatch data-color-name="' + escapeHtml(colorValue) + '" aria-hidden="true"></span>'
        : '';
      var textParts = [];
      if (colorValue) textParts.push('<span data-line-color-name>' + escapeHtml(colorValue) + '</span>');
      if (otherParts.length) textParts.push(escapeHtml(otherParts.join(' · ')));

      return (
        '<div class="cart-drawer__line-meta" data-line-badges data-product-handle="' + escapeHtml(item.handle || '') + '">' +
        swatch +
        '<span class="cart-drawer__line-meta-text">' + textParts.join(' · ') + '</span>' +
        '</div>'
      );
    }

    function renderCartItems(items, cart) {
      const container = drawer.querySelector('[data-cart-drawer-items]');
      if (!container) return;

      // Shared cart-wide discount is consolidated into the footer, so it is
      // excluded from per-line discount rows (mirrors the Liquid render).
      const sharedLineDiscount = getSharedLineDiscount(cart || { items });
      const sharedTitle = sharedLineDiscount ? sharedLineDiscount.title : null;

      container.innerHTML = items
        .map((item, index) => {
          const title = escapeHtml(buildDisplayTitle(item));
          const metaRow = buildLineMeta(item);
          const hasCompare =
            item.original_line_price !== item.final_line_price ||
            item.original_price > item.final_price;
          let priceHtml = `<span class="product-card-horizontal__price">${formatMoney(item.final_line_price)}</span>`;
          if (hasCompare) {
            const compare =
              item.original_line_price !== item.final_line_price
                ? item.original_line_price
                : item.original_price * item.quantity;
            priceHtml = `<span class="product-card-horizontal__price product-card-horizontal__price--sale">${formatMoney(item.final_line_price)}</span><span class="product-card-horizontal__compare-price">${formatMoney(compare)}</span>`;
          }

          const mediaHtml = item.image
            ? `<img src="${getSizedImageUrl(item.image, '240x')}" alt="${title}" class="product-card-horizontal__image">`
            : '<span class="product-card-horizontal__placeholder" aria-hidden="true"><span class="ph-bold ph-package"></span></span>';

          const visibleLineDiscounts = (item.line_level_discount_allocations || []).filter((a) => {
            const t = (a.discount_application && a.discount_application.title) || a.title;
            return t && t !== sharedTitle;
          });
          const lineDiscountsHtml = visibleLineDiscounts.length
            ? `<div class="cart-drawer__line-discounts">${visibleLineDiscounts
                .map((a) => {
                  const t = (a.discount_application && a.discount_application.title) || a.title;
                  return `<span class="cart-drawer__line-discount">${escapeHtml(t)} (-${formatMoney(a.amount)})</span>`;
                })
                .join('')}</div>`
            : '';

          return `
        <article
          class="product-card-horizontal cart-drawer__line-item"
          style="--item-index: ${index};"
          data-product-card-horizontal
          data-cart-drawer-item
          data-line-key="${item.key}"
          data-variant-id="${item.variant_id}"
        >
          <a href="${item.url}" class="product-card-horizontal__media-link" aria-label="${title}">
            <span class="product-card-horizontal__media">${mediaHtml}</span>
          </a>
          <div class="product-card-horizontal__content">
            <div class="product-card-horizontal__top">
              <h3 class="product-card-horizontal__title">${title}</h3>
              ${metaRow}
            </div>
            <div class="product-card-horizontal__bottom">
              <div class="cart-drawer__qty" data-cart-qty>
                <button type="button" class="cart-drawer__qty-btn" data-qty-decrease data-line-key="${item.key}" aria-label="Decrease quantity"><span class="ph-bold ph-minus"></span></button>
                <span class="cart-drawer__qty-value" data-qty-value>${item.quantity}</span>
                <button type="button" class="cart-drawer__qty-btn" data-qty-increase data-line-key="${item.key}" aria-label="Increase quantity"><span class="ph-bold ph-plus"></span></button>
              </div>
              <div class="product-card-horizontal__price-row">${priceHtml}</div>
            </div>
            <button type="button" class="cart-drawer__line-remove" data-remove-item data-line-key="${item.key}">Remove</button>
            ${lineDiscountsHtml}
          </div>
        </article>`;
        })
        .join('');

      attachItemListeners();
      hydrateLineColorBadges();
    }

    // ── Colourway meta hydration ─────────────────────────────────────────
    // Products carry only a colour SLUG (custom.color) — no hex metafield —
    // so the swatch dot + colour name resolve client-side from
    // HealixColors.siblingsRaw(handle) (batched + localStorage-cached): the
    // line item's own entry among its family siblings supplies
    // colorName + hex. Falls back to the option-derived name with a
    // color-swatches.js-resolved dot when HealixColors is unavailable.
    function hydrateLineColorBadges() {
      if (!window.HealixColors || typeof window.HealixColors.siblingsRaw !== 'function') return;
      drawer.querySelectorAll('[data-line-badges]').forEach(function (row) {
        var handle = row.getAttribute('data-product-handle');
        if (!handle || row.dataset.colorHydrated === 'true') return;
        row.dataset.colorHydrated = 'true';
        window.HealixColors.siblingsRaw(handle).then(function (sib) {
          if (!sib || !row.isConnected) return;
          var entries = [].concat(sib.core || [], sib.limited || []);
          var entry = null;
          for (var i = 0; i < entries.length; i++) {
            if (entries[i] && entries[i].handle === handle) { entry = entries[i]; break; }
          }
          if (!entry || !entry.colorName) return;

          // Swatch dot: prefer the exact hex from HealixColors.
          var swatch = row.querySelector('[data-line-swatch]');
          if (!swatch) {
            swatch = document.createElement('span');
            swatch.className = 'cart-drawer__line-swatch';
            swatch.setAttribute('data-line-swatch', '');
            swatch.setAttribute('aria-hidden', 'true');
            row.insertBefore(swatch, row.firstChild);
          }
          if (entry.hex) swatch.style.backgroundColor = entry.hex;

          // Colour name: replace the option-derived text, or prepend it when
          // colour is not a variant option (per-colour products).
          var text = row.querySelector('[data-line-meta-text]') || row.querySelector('.cart-drawer__line-meta-text');
          if (!text) return;
          var nameEl = text.querySelector('[data-line-color-name]');
          if (nameEl) {
            nameEl.textContent = entry.colorName;
          } else {
            var existing = text.textContent.trim();
            text.textContent = '';
            var span = document.createElement('span');
            span.setAttribute('data-line-color-name', '');
            span.textContent = entry.colorName;
            text.appendChild(span);
            if (existing) text.appendChild(document.createTextNode(' \u00b7 ' + existing));
          }
        }).catch(function () { /* no colour info — row still renders fine */ });
      });
    }

    function attachItemListeners() {
      drawer.querySelectorAll('[data-qty-decrease]').forEach((btn) => {
        btn.onclick = async function () {
          if (drawer.classList.contains('is-updating')) return;
          const key = this.dataset.lineKey;
          const valueEl = this.parentElement.querySelector('[data-qty-value]');
          const currentQty = parseInt(valueEl.textContent, 10) || 1;
          const newQty = Math.max(1, currentQty - 1);
          if (newQty === currentQty) return;
          try {
            await updateQuantity(key, newQty);
          } catch (e) {
            console.error('Failed to update quantity:', e);
          }
        };
      });

      drawer.querySelectorAll('[data-qty-increase]').forEach((btn) => {
        btn.onclick = async function () {
          if (drawer.classList.contains('is-updating')) return;
          const key = this.dataset.lineKey;
          const valueEl = this.parentElement.querySelector('[data-qty-value]');
          const newQty = (parseInt(valueEl.textContent, 10) || 1) + 1;
          try {
            await updateQuantity(key, newQty);
          } catch (e) {
            console.error('Failed to update quantity:', e);
          }
        };
      });

      drawer.querySelectorAll('[data-remove-item]').forEach((btn) => {
        btn.onclick = function () {
          if (drawer.classList.contains('is-updating')) return;
          removeItem(this.dataset.lineKey);
        };
      });
    }

    // ---- Upsell variant helpers (mirror shop-the-look's resolver) ----
    function upsellSelEsc(v) { return String(v).replace(/["\\]/g, '\\$&'); }
    function upsellEsc(s) {
      return String(s == null ? '' : s).replace(/[&<>"]/g, (c) =>
        ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]
      );
    }
    function upsellDeriveOptions(prod) {
      return (prod.optionNames || []).map((name, idx) => {
        const seen = {}, values = [];
        (prod.variants || []).forEach((v) => {
          const val = v.options[idx];
          if (val != null && !seen[val]) { seen[val] = 1; values.push(val); }
        });
        return { name, values, idx };
      });
    }
    function upsellValueAvailable(prod, idx, value, chosen) {
      return prod.variants.some((v) => {
        if (!v.available) return false;
        if (String(v.options[idx]) !== String(value)) return false;
        for (let k = 0; k < chosen.length; k++) {
          if (k === idx) continue;
          if (chosen[k] != null && String(v.options[k]) !== String(chosen[k])) return false;
        }
        return true;
      });
    }
    function upsellResolveVariant(prod, chosen) {
      if (chosen.some((c) => c == null)) return null;
      let match = null;
      prod.variants.some((v) => {
        const ok = chosen.every((c, k) => String(v.options[k]) === String(c));
        if (ok) { match = v; return true; }
        return false;
      });
      return match;
    }
    function upsellIsColorOpt(name) { return /colou?r/i.test(name || ''); }
    function upsellSwatchHex(name) {
      try {
        if (window.UntamedColorSwatches && typeof window.UntamedColorSwatches.resolve === 'function') {
          const hex = window.UntamedColorSwatches.resolve(name);
          if (hex) return hex;
        }
      } catch (e) {}
      const n = String(name || '').trim().toLowerCase().replace(/\s+/g, '');
      return /^[a-z]+$/.test(n) ? n : '';
    }

    function openUpsellVariantSheet(prod) {
      const sheetEl = document.querySelector('[data-ui-sheet="cart-upsell"]');
      const body = sheetEl ? sheetEl.querySelector('[data-cart-upsell-sheet-body]') : null;
      if (!body || !window.UISheet) {
        if (prod.defaultVariantId && window.CartAPI) window.CartAPI.addItem(prod.defaultVariantId, 1);
        return;
      }
      const options = upsellDeriveOptions(prod);
      const chosen = options.map((o) => (o.values.length === 1 ? o.values[0] : null));
      const VP = (window.UntamedSheets && window.UntamedSheets.vp) || {};

      function render() {
        const ready = !!upsellResolveVariant(prod, chosen);
        body.innerHTML = window.UntamedSheets.renderVariantPicker([{
          pi: 0, title: prod.title, image: prod.image,
          options: options.map((o) => {
            const color = upsellIsColorOpt(o.name);
            return {
              name: o.name, oi: o.idx, isColor: color,
              selected: color ? chosen[o.idx] : null,
              values: o.values.map((val) => {
                return { value: val, selected: String(chosen[o.idx]) === String(val), available: upsellValueAvailable(prod, o.idx, val, chosen), hex: color ? upsellSwatchHex(val) : null };
              })
            };
          })
        }], {
          addAttr: 'data-cart-upsell-sheet-add',
          addLabel: VP.add || 'Add to bag',
          selectLabel: VP.select || 'Select options',
          ready: ready
        });
        bind();
      }
      function bind() {
        body.querySelectorAll('[data-vp-val]').forEach((btn) => {
          btn.addEventListener('click', () => {
            if (btn.getAttribute('aria-disabled') === 'true') return;
            const oi = parseInt(btn.getAttribute('data-vp-oi'), 10);
            const val = btn.getAttribute('data-vp-val');
            chosen[oi] = String(chosen[oi]) === String(val) ? null : val;
            render();
          });
        });
        const add = body.querySelector('[data-cart-upsell-sheet-add]');
        if (add) add.addEventListener('click', async () => {
          const v = upsellResolveVariant(prod, chosen);
          if (!v || !window.CartAPI) return;
          add.disabled = true;
          try {
            await window.CartAPI.addItem(v.id, 1);
            window.UISheet.close();
          } catch (e) {
            console.error(e);
            add.disabled = false;
          }
        });
      }

      render();
      window.UISheet.open('cart-upsell', { title: prod.title || 'Choose your options' });
    }

    function upsellAddedFeedback(btn) {
      if (!btn) return;
      if (hasGsap()) {
        gsap.fromTo(btn, { rotate: 0 }, { rotate: 360, duration: 0.5, ease: 'power2.out' });
      }
      btn.classList.add('is-added');
      const icon = btn.querySelector('.ph-bold');
      if (icon && window.UntamedIcons) window.UntamedIcons.setIcon(icon, 'check');
      else if (icon) icon.className = 'ph-bold ph-check';
      setTimeout(() => {
        btn.classList.remove('is-added');
        if (icon && window.UntamedIcons) window.UntamedIcons.setIcon(icon, 'plus');
        else if (icon) icon.className = 'ph-bold ph-plus';
        if (hasGsap()) gsap.set(btn, { rotate: 0 });
      }, 1500);
    }

    // CartAPI-only add. Multi-variant products open the shared picker sheet.
    async function quickAddUpsell(btn) {
      const card = btn ? btn.closest('[data-cart-upsell-card]') : null;
      const variantCount = card ? parseInt(card.getAttribute('data-variant-count'), 10) || 1 : 1;

      if (card && variantCount > 1) {
        const dataEl = card.querySelector('[data-cart-upsell-variants]');
        if (dataEl) {
          let prod = null;
          try { prod = JSON.parse(dataEl.textContent); } catch (e) { prod = null; }
          if (prod && (prod.variants || []).length > 1) {
            openUpsellVariantSheet(prod);
            return;
          }
        }
      }

      const variantId = btn ? btn.getAttribute('data-variant-id') : null;
      if (!variantId || !window.CartAPI) return;
      try {
        await window.CartAPI.addItem(variantId, 1);
      } catch (e) {
        console.error(e);
        return;
      }
      upsellAddedFeedback(btn);
    }

    function bindSheetEvents() {
      // Open/close is handled by ui-sheet.js via data-ui-sheet-trigger/close.
      // Here we only bind the sheet *actions* (note autosave, coupon, shipping).
      const noteArea = drawer.querySelector('[data-cart-note]');
      if (noteArea) {
        noteArea.oninput = function () {
          clearTimeout(notesSaveTimeout);
          notesSaveTimeout = setTimeout(() => saveNote(this.value), 500);
        };
      }
      const couponInput = drawer.querySelector('[data-coupon-input]');
      const couponApply = drawer.querySelector('[data-coupon-apply]');
      if (couponApply) {
        couponApply.onclick = () => applyDiscountCode(couponInput?.value || '');
      }
      if (couponInput) {
        couponInput.oninput = () => clearCouponError();
        couponInput.onkeydown = (e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            applyDiscountCode(couponInput.value.trim());
          }
        };
      }
      drawer.querySelectorAll('[data-coupon-apply-code]').forEach((btn) => {
        btn.onclick = () => applyDiscountCode(btn.getAttribute('data-coupon-apply-code'));
      });
      const shipCalc = drawer.querySelector('[data-shipping-calculate]');
      if (shipCalc) {
        shipCalc.onclick = calculateShippingRates;
      }
      drawer.querySelectorAll('[data-cart-upsell-add]').forEach((btn) => {
        btn.onclick = (e) => {
          e.preventDefault();
          if (drawer.classList.contains('is-updating')) return;
          quickAddUpsell(btn);
        };
      });
    }

    function bindEventsOnce() {
      drawer.addEventListener('click', (e) => {
        if (e.target.closest('[data-cart-close]')) {
          e.preventDefault();
          closeDrawer();
          return;
        }
        if (e.target.closest('[data-cart-overlay]')) {
          closeDrawer();
        }
      });
    }

    function formatMoney(cents) {
      if (window.formatMoney) return window.formatMoney(cents);
      if (window.formatMoneyFallback) return window.formatMoneyFallback(cents);
      return (Number(cents || 0) / 100).toFixed(2);
    }

    function getSizedImageUrl(url, size) {
      if (!url) return '';
      return url.replace(/\.(jpg|jpeg|png|gif|webp)/i, `_${size}.$1`);
    }

    function escapeHtml(text) {
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    }

    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      // If a ui-sheet (note / shipping / coupon / upsell / quick-view) is open,
      // let ui-sheet.js handle Escape — don't also close the whole drawer.
      if (document.documentElement.classList.contains('ui-sheet-open')) return;
      if (drawer.getAttribute('aria-hidden') === 'false') closeDrawer();
    });

    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-cart-toggle]')) {
        e.preventDefault();
        toggleDrawer();
      }
    });

    window.addEventListener('cart:open', openDrawer);
    window.addEventListener('cart:close', closeDrawer);
    window.addEventListener('cart:toggle', toggleDrawer);

    window.addEventListener('cart:changing', () => setDrawerUpdating(true));

    window.addEventListener('cart:updated', () => refreshDrawerFromCart());

    window.addEventListener('cart:error', () => setDrawerUpdating(false));

    window.addEventListener('cart:item-added', () => {
      if (openOnAdd) openDrawer();
    });

    bindEventsOnce();
    bindSheetEvents();
    attachItemListeners();
    hydrateLineColorBadges();
    clearDrawerShellGsap();
    initGsapLayout();
    if (window.UntamedIcons?.refresh) window.UntamedIcons.refresh();
    updateHeaderCountFromCart();

    if (window.gsapReady && typeof gsap === 'undefined') {
      window.gsapReady.then(initGsapLayout);
    }
  }
})();



// complete your look js 

(function () {
  "use strict";

  /* ============================================================
     HEALIX — COMPLETE YOUR LOOK
     MULTI-COLOR CART + SIZE POPUP
     ============================================================ */

  var DEBUG = true;

  var ROOT_SELECTOR =   "[data-healix-complete-look-all]";

  /* ============================================================
     CATEGORY ORDER
     ============================================================ */

  var CATEGORY_ORDER = [
    "accessories",
    "tops",
    "pants",
     "jumpsuits",
      "jackets",
      "underscrubs"
  ];

  var CATEGORY_LABELS = {
    pants: "Pants",
    accessories: "Accessories",
    jackets: "Jackets",
    jumpsuits: "Jumpsuits",
    underscrubs: "Underscrubs",
    tops: "Tops"
  };

  /* ============================================================
     CACHE / STATE
     ============================================================ */

  var collectionCache = {};

  var scheduleTimer = null;

  var renderGeneration = 0;

  var initialized = false;

  var activePopup = null;

  /* ============================================================
     COLORS
     ============================================================ */

  var COLOR_NAMES = [
    "carbon calm",
    "blush",
    "citrus",
    "clinic blue",
    "cloud",
    "core blue",
    "code black",
    "golden hour",
    "lavender",
    "kind mauve",
    "lavender stat",
    "lime",
    "night grape",
    "midnight surge",
    "mint",
    "olive drip",
    "pecan drift",
    "peri hustle",
    "vital navy",
    "plum pulse",
    "pulse pink",
    "rapid red",
    "rose",
    "shift sapphire",
    "surgical teal",
    "teal tonic",
    "white"
  ];

  var COLOR_ALIASES = {
    "clinical blue": "clinic blue",
    "clinic blue": "clinic blue",
    "vitals navy": "vital navy",
    "vital navy": "vital navy"
  };

  /* ============================================================
     DEBUG
     ============================================================ */

  function log() {
    if (!DEBUG) {
      return;
    }

    var args = Array.prototype.slice.call(arguments);

    console.log.apply(
      console,
      ["[Healix Complete Look]"].concat(args)
    );
  }

  /* ============================================================
     SHOPIFY ROOT
     ============================================================ */

  function shopifyRoot() {
    if (
      window.Shopify &&
      window.Shopify.routes &&
      window.Shopify.routes.root
    ) {
      return window.Shopify.routes.root;
    }

    return "/";
  }

  /* ============================================================
     TEXT HELPERS
     ============================================================ */

  function normalizeText(value) {
    if (
      value === null ||
      value === undefined
    ) {
      return "";
    }

    return String(value)
      .toLowerCase()
      .replace(/[’']/g, "")
      .replace(/[-_]+/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  }

  function compactText(value) {
    return normalizeText(value)
      .replace(/[^a-z0-9]/g, "");
  }

  function canonicalColor(color) {
    var normalized = normalizeText(color);

    return (
      COLOR_ALIASES[normalized] ||
      normalized
    );
  }

  function sentenceCase(value) {
    if (!value) {
      return "";
    }

    var text = String(value)
      .toLowerCase()
      .replace(/\s+/g, " ")
      .trim();

    if (!text) {
      return "";
    }

    return (
      text.charAt(0).toUpperCase() +
      text.slice(1)
    );
  }

  /* ============================================================
     COLOR CANDIDATES
     ============================================================ */

  function getColorCandidates() {
    var candidates = COLOR_NAMES.slice();

    Object.keys(COLOR_ALIASES).forEach(
      function (alias) {
        if (
          candidates.indexOf(alias) === -1
        ) {
          candidates.push(alias);
        }
      }
    );

    candidates.sort(
      function (a, b) {
        return (
          compactText(b).length -
          compactText(a).length
        );
      }
    );

    return candidates;
  }

  var COLOR_CANDIDATES =
    getColorCandidates();

  /* ============================================================
     COLOR MATCHING
     ============================================================ */

  function matchColor(value) {
    if (!value) {
      return null;
    }

    var normalized = compactText(value);

    for (
      var i = 0;
      i < COLOR_CANDIDATES.length;
      i++
    ) {
      var candidate =
        COLOR_CANDIDATES[i];

      var candidateCompact =
        compactText(candidate);

      if (
        normalized.indexOf(
          candidateCompact
        ) !== -1
      ) {
        return canonicalColor(candidate);
      }
    }

    return null;
  }

  function colorValueMatches(
    value,
    targetColor
  ) {
    if (!value || !targetColor) {
      return false;
    }

    var detected = matchColor(value);

    if (!detected) {
      return false;
    }

    return (
      canonicalColor(detected) ===
      canonicalColor(targetColor)
    );
  }

  /* ============================================================
     DETECT COLOR
     ============================================================ */

  function detectColor(item) {
    if (!item) {
      return null;
    }

    /* COLOR / COLOUR OPTION */

    if (
      Array.isArray(
        item.options_with_values
      )
    ) {
      for (
        var i = 0;
        i < item.options_with_values.length;
        i++
      ) {
        var option =
          item.options_with_values[i];

        if (!option) {
          continue;
        }

        var optionName =
          normalizeText(option.name);

        if (
          optionName === "color" ||
          optionName === "colour"
        ) {
          var optionColor =
            matchColor(option.value);

          if (optionColor) {
            return optionColor;
          }
        }
      }
    }

    /* HANDLE */

    var color =
      matchColor(item.handle);

    if (color) {
      return color;
    }

    /* URL */

    color =
      matchColor(item.url);

    if (color) {
      return color;
    }

    /* VARIANT TITLE */

    color =
      matchColor(item.variant_title);

    if (color) {
      return color;
    }

    /* PRODUCT TITLE */

    color =
      matchColor(
        item.product_title ||
        item.title
      );

    if (color) {
      return color;
    }

    /* IMAGE ALT */

    if (
      item.featured_image &&
      item.featured_image.alt
    ) {
      color =
        matchColor(
          item.featured_image.alt
        );

      if (color) {
        return color;
      }
    }

    return null;
  }

  /* ============================================================
     GET ALL CART COLORS
     ============================================================ */

  function getCartColors(cart) {
    var colors = [];

    if (
      !cart ||
      !Array.isArray(cart.items)
    ) {
      return colors;
    }

    cart.items.forEach(
      function (item) {
        var color =
          detectColor(item);

        if (!color) {
          return;
        }

        color =
          canonicalColor(color);

        if (
          colors.indexOf(color) === -1
        ) {
          colors.push(color);
        }
      }
    );

    return colors;
  }

  /* ============================================================
     DETECT GENDER
     ============================================================ */

  function detectGender(item) {
    if (!item) {
      return null;
    }

    var text =
      normalizeText(
        [
          item.handle,
          item.product_title,
          item.title,
          item.product_type,
          item.url
        ].join(" ")
      );

    /*
     * WOMEN FIRST because "women" contains "men".
     */

    if (
      /\b(women|womens)\b/.test(text)
    ) {
      return "women";
    }

    if (
      /\b(men|mens)\b/.test(text)
    ) {
      return "men";
    }

    return null;
  }

  /* ============================================================
     COLLECTION HANDLE
     ============================================================ */

  function makeCollectionHandle(
    color,
    gender
  ) {
    var base =
      canonicalColor(color)
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");

    return (
      base +
      "-" +
      (
        gender === "men"
          ? "men"
          : "women"
      )
    );
  }

  /* ============================================================
     COLLECTION HANDLES FOR ONE CART ITEM
     ============================================================ */
function getCollectionHandles(
  color,
  cartItem
) {
  var gender =
    detectGender(cartItem);

  var canonical =
    canonicalColor(color);

  /*
   * CLINIC BLUE SPECIAL CASE
   *
   * Display/color detection uses:
   *   clinic blue
   *
   * But the Shopify collection handles are:
   *   clinical-blue-women
   *   clinical-blue-men
   *
   * Try the real collection handle first.
   * Keep clinic-blue as a fallback.
   */
  if (canonical === "clinic blue") {

    if (gender === "women") {
      return [
        "clinical-blue-women",
        "clinic-blue-women"
      ];
    }

    if (gender === "men") {
      return [
        "clinical-blue-men",
        "clinic-blue-men"
      ];
    }

    return [
      "clinical-blue-women",
      "clinic-blue-women",
      "clinical-blue-men",
      "clinic-blue-men"
    ];
  }

  /*
   * NORMAL COLORS
   */
  if (gender === "women") {
    return [
      makeCollectionHandle(
        color,
        "women"
      )
    ];
  }

  if (gender === "men") {
    return [
      makeCollectionHandle(
        color,
        "men"
      )
    ];
  }

  return [
    makeCollectionHandle(
      color,
      "women"
    ),
    makeCollectionHandle(
      color,
      "men"
    )
  ];
}

  /* ============================================================
     GET CART
     ============================================================ */

  async function getCart() {
    var response =
      await fetch(
        shopifyRoot() + "cart.js",
        {
          method: "GET",
          credentials: "same-origin",
          headers: {
            Accept:
              "application/json"
          },
          cache: "no-store"
        }
      );

    if (!response.ok) {
      throw new Error(
        "Cart request failed"
      );
    }

    return await response.json();
  }

  /* ============================================================
     GET COLLECTION PRODUCTS
     ============================================================ */

  async function getCollectionProducts(
    handle
  ) {
    if (!handle) {
      return [];
    }

    if (
      Object.prototype.hasOwnProperty.call(
        collectionCache,
        handle
      )
    ) {
      log(
        "Using cached collection:",
        handle
      );

      return collectionCache[handle];
    }

    var allProducts = [];

    var page = 1;

    var limit = 250;

    var maxPages = 20;

    var requestSucceeded = false;

    while (
      page <= maxPages
    ) {
      var url =
        shopifyRoot() +
        "collections/" +
        encodeURIComponent(handle) +
        "/products.json?limit=" +
        limit +
        "&page=" +
        page;

      log(
        "Loading collection:",
        handle,
        "page:",
        page
      );

      try {
        var response =
          await fetch(
            url,
            {
              method: "GET",
              credentials: "same-origin",
              headers: {
                Accept:
                  "application/json"
              },
              cache: "no-store"
            }
          );

        if (!response.ok) {
          log(
            "Collection request failed:",
            handle,
            response.status
          );

          break;
        }

        requestSucceeded = true;

        var data =
          await response.json();

        var products =
          Array.isArray(data.products)
            ? data.products
            : [];

        if (!products.length) {
          break;
        }

        allProducts =
          allProducts.concat(
            products
          );

        if (
          products.length < limit
        ) {
          break;
        }

        page++;

      } catch (error) {
        console.error(
          "[Healix Complete Look]",
          error
        );

        break;
      }
    }

    var uniqueProducts = [];

    var seen = {};

    allProducts.forEach(
      function (product) {
        if (!product) {
          return;
        }

        var key =
          product.id
            ? String(product.id)
            : "handle:" +
              String(
                product.handle || ""
              );

        if (seen[key]) {
          return;
        }

        seen[key] = true;

        uniqueProducts.push(
          product
        );
      }
    );

    if (requestSucceeded) {
      collectionCache[handle] =
        uniqueProducts;
    }

    log(
      "Collection loaded:",
      handle,
      "products:",
      uniqueProducts.length
    );

    return uniqueProducts;
  }

  /* ============================================================
     CART VARIANT IDS
     ============================================================ */

  function getCartVariantIds(cart) {
    var ids = {};

    if (
      !cart ||
      !Array.isArray(cart.items)
    ) {
      return ids;
    }

    cart.items.forEach(
      function (item) {
        if (item.variant_id) {
          ids[
            String(item.variant_id)
          ] = true;
        }
      }
    );

    return ids;
  }

  /* ============================================================
     PRODUCT AVAILABLE
     ============================================================ */

  function productIsAvailable(
    product
  ) {
    if (!product) {
      return false;
    }

    if (
      product.available === false
    ) {
      return false;
    }

    if (
      !Array.isArray(
        product.variants
      )
    ) {
      return false;
    }

    return product.variants.some(
      function (variant) {
        return (
          variant &&
          variant.available === true
        );
      }
    );
  }

  /* ============================================================
     OPTION INDEX HELPERS
     ============================================================ */

  function getColorOptionIndex(
    product
  ) {
    if (
      !product ||
      !Array.isArray(product.options)
    ) {
      return -1;
    }

    for (
      var i = 0;
      i < product.options.length;
      i++
    ) {
      var option =
        product.options[i];

      if (!option) {
        continue;
      }

      var name =
        normalizeText(
          option.name
        );

      if (
        name === "color" ||
        name === "colour"
      ) {
        return i;
      }
    }

    return -1;
  }

  function getSizeOptionIndex(
    product
  ) {
    if (
      !product ||
      !Array.isArray(product.options)
    ) {
      return -1;
    }

    for (
      var i = 0;
      i < product.options.length;
      i++
    ) {
      var option =
        product.options[i];

      if (!option) {
        continue;
      }

      var name =
        normalizeText(
          option.name
        );

      if (
        name === "size" ||
        name === "sizes"
      ) {
        return i;
      }
    }

    return -1;
  }
/* ============================================================
   LENGTH OPTION INDEX
   ============================================================ */

function getLengthOptionIndex(product) {
  if (
    !product ||
    !Array.isArray(product.options)
  ) {
    return -1;
  }

  for (var i = 0; i < product.options.length; i++) {
    var option = product.options[i];

    if (!option) {
      continue;
    }

    var name = normalizeText(option.name);

    if (
      name === "length" ||
      name === "lengths"
    ) {
      return i;
    }
  }

  return -1;
}
  /* ============================================================
     AVAILABLE COLOR VARIANTS
     ============================================================ */

  function getAvailableColorVariants(
    product,
    color
  ) {
    if (
      !product ||
      !Array.isArray(
        product.variants
      )
    ) {
      return [];
    }

    var available =
      product.variants.filter(
        function (variant) {
          return (
            variant &&
            variant.available === true
          );
        }
      );

    var colorIndex =
      getColorOptionIndex(
        product
      );

    /*
     * Product is already inside a color-specific
     * collection, so if it has no Color option,
     * every available variant belongs to that
     * collection color.
     */

    if (colorIndex === -1) {
      return available;
    }

    var colorKey =
      "option" +
      (colorIndex + 1);

    return available.filter(
      function (variant) {
        return colorValueMatches(
          variant[colorKey],
          color
        );
      }
    );
  }

  /* ============================================================
     AVAILABLE SIZE VARIANTS
     ============================================================ */

  function getAvailableSizeVariants(
    product,
    color
  ) {
    var matched =
      getAvailableColorVariants(
        product,
        color
      );

    var sizeIndex =
      getSizeOptionIndex(
        product
      );

    /*
     * NO SIZE OPTION
     */

    if (sizeIndex === -1) {
      return {
        hasSizeOption: false,
        options: [],
        variants: matched
      };
    }

    var sizeKey =
      "option" +
      (sizeIndex + 1);

    var bySize = {};

    var options = [];

    matched.forEach(
      function (variant) {
        var size =
          variant[sizeKey];

        if (!size) {
          return;
        }

        var normalized =
          normalizeText(size);

        if (!normalized) {
          return;
        }

        /*
         * Only one entry per available size.
         */

        if (
          bySize[normalized]
        ) {
          return;
        }

        bySize[normalized] = true;

        options.push({
          value: String(size),
          variant: variant
        });
      }
    );

    return {
      hasSizeOption: true,
      options: options,
      variants: matched
    };
  }

  /* ============================================================
     CATEGORY DETECTION
     ============================================================ */

  function detectCategory(
    product
  ) {
    if (!product) {
      return null;
    }

    var title =
      normalizeText(
        product.title || ""
      );

    var type =
      normalizeText(
        product.product_type || ""
      );

    var text =
      title +
      " " +
      type;

    if (
      text.indexOf("jumpsuit") !== -1
    ) {
      return "jumpsuits";
    }

    if (
      text.indexOf("jacket") !== -1
    ) {
      return "jackets";
    }

    if (
      text.indexOf("underscrub") !== -1 ||
      text.indexOf("under scrub") !== -1
    ) {
      return "underscrubs";
    }

    if (
      /\b(pant|pants|jogger|joggers)\b/.test(
        text
      )
    ) {
      return "pants";
    }

    if (
      text.indexOf("accessor") !== -1 ||
      text.indexOf("headband") !== -1 ||
      text.indexOf("scrunchie") !== -1 ||
      /\bcap\b/.test(text)
    ) {
      return "accessories";
    }

    if (
      /\btop\b/.test(title) ||
      /\btops\b/.test(title) ||
      type.indexOf("top") !== -1 ||
      text.indexOf("scrub top") !== -1
    ) {
      return "tops";
    }

    return null;
  }

  /* ============================================================
     HIGH RES IMAGE
     ============================================================ */

 function getHighResImageUrl(url, width) {
  if (!url) {
    return "";
  }

  var imageUrl = String(url).trim();

  if (!imageUrl) {
    return "";
  }

  var isShopifyImage =
    imageUrl.indexOf("cdn.shopify.com") !== -1 ||
    imageUrl.indexOf("shopify.com") !== -1;

  if (!isShopifyImage) {
    return imageUrl;
  }

  /*
   * Remove old Shopify size suffixes such as:
   * _300x
   * _500x
   * _small
   * _medium
   * _large
   */
  imageUrl = imageUrl.replace(
    /_(pico|icon|thumb|small|medium|large|grande|\d+x\d*|\d+x)\.(jpg|jpeg|png|webp|avif)(?=($|\?))/i,
    ".$2"
  );

  /*
   * Remove existing width parameter.
   */
  imageUrl = imageUrl.replace(
    /([?&])width=\d+/gi,
    ""
  );

  /*
   * Clean up ? / & after removing width.
   */
  imageUrl = imageUrl.replace(
    /\?&/g,
    "?"
  );

  imageUrl = imageUrl.replace(
    /[?&]$/,
    ""
  );

  /*
   * Force a high-resolution Shopify CDN image.
   */
  imageUrl +=
    (imageUrl.indexOf("?") !== -1 ? "&" : "?") +
    "width=" +
    (width || 2000);

  return imageUrl;
}

  /* ============================================================
     IMAGE URL
     ============================================================ */

  function getImageUrl(
    product,
    variant
  ) {
    var originalUrl = "";

    if (
      variant &&
      variant.featured_image
    ) {
      if (
        typeof variant.featured_image ===
        "string"
      ) {
        originalUrl =
          variant.featured_image;
      } else if (
        variant.featured_image.src
      ) {
        originalUrl =
          variant.featured_image.src;
      }
    }

    if (!originalUrl) {
      if (
        product &&
        product.featured_image
      ) {
        if (
          typeof product.featured_image ===
          "string"
        ) {
          originalUrl =
            product.featured_image;
        } else if (
          product.featured_image.src
        ) {
          originalUrl =
            product.featured_image.src;
        }
      }
    }

    if (!originalUrl) {
      if (
        product &&
        Array.isArray(product.images) &&
        product.images.length
      ) {
        var image =
          product.images[0];

        if (
          typeof image === "string"
        ) {
          originalUrl = image;
        } else if (
          image &&
          image.src
        ) {
          originalUrl =
            image.src;
        }
      }
    }

    if (!originalUrl) {
      return "";
    }

    return getHighResImageUrl(
      originalUrl,
      1200
    );
  }

  function getImageSrcSet(
    url
  ) {
    if (!url) {
      return "";
    }

    var widths = [
      300,
      500,
      800,
      1200
    ];

    return widths
      .map(
        function (width) {
          return (
            getHighResImageUrl(
              url,
              width
            ) +
            " " +
            width +
            "w"
          );
        }
      )
      .join(", ");
  }

  /* ============================================================
     PRODUCT URL
     ============================================================ */

  function getProductUrl(
    product
  ) {
    if (
      product &&
      product.url
    ) {
      return product.url;
    }

    if (
      product &&
      product.handle
    ) {
      return (
        shopifyRoot() +
        "products/" +
        encodeURIComponent(
          product.handle
        )
      );
    }

    return "#";
  }

  /* ============================================================
     CURRENCY
     ============================================================ */

  function getActiveCurrencyCode() {
    if (
      window.Shopify &&
      window.Shopify.currency &&
      window.Shopify.currency.active
    ) {
      return String(
        window.Shopify.currency.active
      ).toUpperCase();
    }

    var currencyMeta =
      document.querySelector(
        'meta[property="og:price:currency"]'
      );

    if (
      currencyMeta &&
      currencyMeta.content
    ) {
      return String(
        currencyMeta.content
      ).toUpperCase();
    }

    return "INR";
  }

  function getCurrencySymbol(
    currencyCode
  ) {
    if (!currencyCode) {
      return "";
    }

    try {
      var locale =
        document.documentElement.lang ||
        "en-IN";

      var parts =
        new Intl.NumberFormat(
          locale,
          {
            style: "currency",
            currency:
              currencyCode,
            currencyDisplay:
              "symbol"
          }
        ).formatToParts(1);

      for (
        var i = 0;
        i < parts.length;
        i++
      ) {
        if (
          parts[i].type ===
          "currency"
        ) {
          return parts[i].value;
        }
      }
    } catch (e) {}

    return "";
  }

  function formatCatalogMoney(
    value
  ) {
    if (
      value === null ||
      value === undefined ||
      value === ""
    ) {
      return "";
    }

    var number =
      Number(value);

    if (!isFinite(number)) {
      return "";
    }

    var currencyCode =
      getActiveCurrencyCode();

    try {
      var locale =
        document.documentElement.lang ||
        "en-IN";

      return new Intl.NumberFormat(
        locale,
        {
          style: "currency",
          currency:
            currencyCode,
          currencyDisplay:
            "symbol",
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }
      ).format(number);
    } catch (e) {}

    if (
      window.Shopify &&
      typeof window.Shopify.formatMoney ===
      "function"
    ) {
      try {
        return window.Shopify.formatMoney(
          Math.round(
            number * 100
          )
        );
      } catch (error) {}
    }

    var symbol =
      getCurrencySymbol(
        currencyCode
      );

    return (
      symbol +
      number.toFixed(2)
    );
  }

  /* ============================================================
     ESCAPE HTML
     ============================================================ */

  function escapeHtml(
    value
  ) {
    if (
      value === null ||
      value === undefined
    ) {
      return "";
    }

    return String(value)
      .replace(
        /&/g,
        "&amp;"
      )
      .replace(
        /</g,
        "&lt;"
      )
      .replace(
        />/g,
        "&gt;"
      )
      .replace(
        /"/g,
        "&quot;"
      )
      .replace(
        /'/g,
        "&#039;"
      );
  }

  /* ============================================================
     PREPARE PRODUCTS
     IMPORTANT:
     SAME PRODUCT CAN APPEAR FOR MULTIPLE CART COLORS.
     ============================================================ */

/* ============================================================
   PREPARE PRODUCTS
   ============================================================

   EXACT RULE:

   1 OR 2 CART COLORS
   ------------------
   Show ALL available related products.

   3+ CART COLORS
   --------------
   Show MAXIMUM 3 PRODUCTS
   PER CATEGORY
   PER COLOR.

   Example:

   Cart colors:
   - Golden Hour
   - Night Grape
   - Olive Drip

   Golden Hour:
      Pants       -> max 3
      Tops        -> max 3
      Accessories -> max 3
      Jackets     -> max 3
      Jumpsuits   -> max 3
      Underscrubs -> max 3

   Night Grape:
      Pants       -> max 3
      Tops        -> max 3
      Accessories -> max 3
      Jackets     -> max 3
      Jumpsuits   -> max 3
      Underscrubs -> max 3

   Olive Drip:
      Pants       -> max 3
      Tops        -> max 3
      Accessories -> max 3
      Jackets     -> max 3
      Jumpsuits   -> max 3
      Underscrubs -> max 3

   IMPORTANT:
   The limit is NOT 3 products per color.

   It is:

       COLOR + CATEGORY = maximum 3

   So:

       Golden Hour + Pants = 3
       Golden Hour + Tops = 3
       Golden Hour + Accessories = 3

   etc.

   Keeps:
   - Size
   - Length
   - Clearance pricing
   - Existing cart checks
   - Existing category detection
   ============================================================ */

function prepareProducts(
  cart,
  colorCollections
) {
  var cartVariantIds =
    getCartVariantIds(cart);

  /*
   * ============================================================
   * CATEGORY ARRAYS
   * ============================================================
   */

  var categories = {
    pants: [],
    accessories: [],
    jackets: [],
    jumpsuits: [],
    underscrubs: [],
    tops: []
  };

  /*
   * ============================================================
   * DUPLICATE TRACKING
   * ============================================================
   *
   * Same product + same color
   * should only appear once.
   */

  var seenProductColors = {};

  /*
   * ============================================================
   * FIND UNIQUE CART COLORS
   * ============================================================
   */

  var cartColors = {};

  colorCollections.forEach(
    function (colorData) {
      if (!colorData) {
        return;
      }

      var color =
        canonicalColor(
          colorData.color
        );

      if (!color) {
        return;
      }

      cartColors[color] = true;
    }
  );

  var uniqueCartColors =
    Object.keys(
      cartColors
    );

  var cartColorCount =
    uniqueCartColors.length;

  /*
   * ============================================================
   * LIMIT MODE
   * ============================================================
   *
   * 1-2 colors:
   *     SHOW EVERYTHING
   *
   * 3+ colors:
   *     MAX 3 PER CATEGORY PER COLOR
   * ============================================================
   */

  var useCategoryLimit =
    cartColorCount >= 3;

  var MAX_PRODUCTS_PER_CATEGORY =
    3;

  /*
   * ============================================================
   * CATEGORY + COLOR COUNTERS
   * ============================================================
   *
   * Structure:
   *
   * colorCategoryCounts = {
   *
   *   "golden hour": {
   *      pants: 0,
   *      tops: 0,
   *      accessories: 0,
   *      jackets: 0,
   *      jumpsuits: 0,
   *      underscrubs: 0
   *   },
   *
   *   "night grape": {
   *      pants: 0,
   *      tops: 0,
   *      ...
   *   }
   *
   * }
   */

  var colorCategoryCounts = {};

  uniqueCartColors.forEach(
    function (color) {

      colorCategoryCounts[color] = {
        pants: 0,
        accessories: 0,
        jackets: 0,
        jumpsuits: 0,
        underscrubs: 0,
        tops: 0
      };

    }
  );

  /*
   * ============================================================
   * DEBUG
   * ============================================================
   */

  log(
    "================================================"
  );

  log(
    "COMPLETE YOUR LOOK"
  );

  log(
    "CART COLOR COUNT:",
    cartColorCount
  );

  log(
    "CART COLORS:",
    uniqueCartColors
  );

  log(
    "LIMIT MODE:",
    useCategoryLimit
      ? "MAX 3 PER CATEGORY PER COLOR"
      : "SHOW ALL RELATED PRODUCTS"

  );

  log(
    "================================================"
  );

  /*
   * ============================================================
   * PROCESS EVERY CART COLOR
   * ============================================================
   */

  colorCollections.forEach(
    function (colorData) {

      if (!colorData) {
        return;
      }

      var color =
        canonicalColor(
          colorData.color
        );

      var products =
        Array.isArray(
          colorData.products
        )
          ? colorData.products
          : [];

      if (!color) {
        return;
      }

      /*
       * Safety initialization.
       */

      if (
        !colorCategoryCounts[color]
      ) {
        colorCategoryCounts[color] = {
          pants: 0,
          accessories: 0,
          jackets: 0,
          jumpsuits: 0,
          underscrubs: 0,
          tops: 0
        };
      }

      /*
       * ========================================================
       * PROCESS PRODUCTS
       * ========================================================
       */

      products.forEach(
        function (product) {

          if (!product) {
            return;
          }

          /*
           * ======================================================
           * PRODUCT KEY
           * ======================================================
           */

          var productKey =
            product.id
              ? String(
                  product.id
                )
              : String(
                  product.handle || ""
                );

          var recommendationKey =
            productKey +
            "::" +
            color;

          /*
           * ======================================================
           * DUPLICATE CHECK
           * ======================================================
           */

          if (
            seenProductColors[
              recommendationKey
            ]
          ) {
            return;
          }

          /*
           * ======================================================
           * PRODUCT AVAILABILITY
           * ======================================================
           */

          if (
            !productIsAvailable(
              product
            )
          ) {
            return;
          }

          /*
           * ======================================================
           * GET AVAILABLE VARIANTS
           * ======================================================
           *
           * This keeps your existing:
           *
           * - Color
           * - Size
           * - Length
           *
           * handling.
           */

          var sizeInfo =
            getAvailableSizeVariants(
              product,
              color
            );

          if (
            !sizeInfo ||
            !Array.isArray(
              sizeInfo.variants
            ) ||
            !sizeInfo.variants.length
          ) {
            return;
          }

          /*
           * ======================================================
           * INITIAL VARIANT
           * ======================================================
           */

          var initialVariant =
            sizeInfo.hasSizeOption
              ? (
                  sizeInfo.options &&
                  sizeInfo.options.length
                    ? sizeInfo.options[0].variant
                    : null
                )
              : sizeInfo.variants[0];

          if (!initialVariant) {
            return;
          }

          /*
           * ======================================================
           * CATEGORY
           * ======================================================
           */

          var category =
            detectCategory(
              product
            );

          if (!category) {

            log(
              "Skipped unknown category:",
              product.title
            );

            return;
          }

          /*
           * ======================================================
           * IMPORTANT:
           * LIMIT IS CATEGORY + COLOR
           * ======================================================
           *
           * Example:
           *
           * Golden Hour + Pants = 3
           *
           * But:
           *
           * Golden Hour + Tops
           * can ALSO have 3.
           *
           * Golden Hour + Accessories
           * can ALSO have 3.
           */

          if (
            useCategoryLimit &&
            colorCategoryCounts[color] &&
            typeof colorCategoryCounts[color][category] ===
              "number" &&
            colorCategoryCounts[color][category] >=
              MAX_PRODUCTS_PER_CATEGORY
          ) {

            log(
              "Category limit reached:",
              color,
              category
            );

            return;
          }

          /*
           * ======================================================
           * EXACT VARIANT ALREADY IN CART
           * ======================================================
           *
           * For products without size:
           * don't show the exact variant already in cart.
           *
           * For size products:
           * keep it because shopper can select another size.
           */

          if (
            initialVariant.id &&
            cartVariantIds[
              String(
                initialVariant.id
              )
            ] &&
            !sizeInfo.hasSizeOption
          ) {

            log(
              "Skipped exact variant already in cart:",
              product.title,
              initialVariant.id
            );

            return;
          }

          /*
           * ======================================================
           * IMAGE
           * ======================================================
           */

          var image =
            getImageUrl(
              product,
              initialVariant
            );

          /*
           * ======================================================
           * PRICE
           * ======================================================
           */

          var price =
            Number(
              initialVariant.price || 0
            );

          var compareAtPrice =
            Number(
              initialVariant.compare_at_price ||
              0
            );

          /*
           * ======================================================
           * CLEARANCE
           * ======================================================
           */

          var isClearance =
            compareAtPrice >
              price &&
            price > 0;

          /*
           * ======================================================
           * ADD PRODUCT TO CATEGORY
           * ======================================================
           */

          categories[
            category
          ].push({

            product:
              product,

            color:
              color,

            variant:
              initialVariant,

            image:
              image,

            price:
              price,

            compareAtPrice:
              compareAtPrice,

            isClearance:
              isClearance,

            hasSizeOption:
              sizeInfo.hasSizeOption,

            sizeOptions:
              sizeInfo.options

          });

          /*
           * ======================================================
           * MARK PRODUCT AS USED
           * ======================================================
           */

          seenProductColors[
            recommendationKey
          ] = true;

          /*
           * ======================================================
           * INCREMENT CATEGORY + COLOR COUNTER
           * ======================================================
           */

          if (
            !colorCategoryCounts[color]
          ) {
            colorCategoryCounts[color] = {};
          }

          if (
            typeof colorCategoryCounts[color][category] !==
            "number"
          ) {
            colorCategoryCounts[color][category] =
              0;
          }

          colorCategoryCounts[color][category]++;

          /*
           * ======================================================
           * DEBUG
           * ======================================================
           */

          log(
            "ADDED RECOMMENDATION:",
            product.title,
            "| COLOR:",
            color,
            "| CATEGORY:",
            category,
            "| COUNT:",
            colorCategoryCounts[color][category],
            useCategoryLimit
              ? "/3"
              : "(NO LIMIT)"
          );

        }
      );

    }
  );

  /*
   * ============================================================
   * DEBUG — FINAL COUNTS
   * ============================================================
   */

  log(
    "================================================"
  );

  log(
    "FINAL COLOR + CATEGORY COUNTS:",
    colorCategoryCounts
  );

  /*
   * Example output:
   *
   * {
   *   "golden hour": {
   *      pants: 3,
   *      tops: 3,
   *      accessories: 3,
   *      jackets: 3
   *   },
   *
   *   "night grape": {
   *      pants: 3,
   *      tops: 3,
   *      accessories: 3,
   *      jackets: 3
   *   }
   * }
   */

  var totalRecommendations = 0;

  CATEGORY_ORDER.forEach(
    function (category) {

      var count =
        categories[category]
          ? categories[category].length
          : 0;

      totalRecommendations +=
        count;

      log(
        "CATEGORY:",
        category,
        "| TOTAL:",
        count
      );

    }
  );

  log(
    "TOTAL COMPLETE YOUR LOOK PRODUCTS:",
    totalRecommendations
  );

  log(
    "================================================"
  );

  return categories;
}

  /* ============================================================
     LOADING
     ============================================================ */

  /* Clearance pricing: reuse the same flash-sale feed (HealixColors) that Complete the Set product cards use */
var flashSalesByHandle = {};

function loadFlashSales() {
  var HC = window.HealixColors;
  if (!HC || typeof HC.deals !== "function") return Promise.resolve();
  return HC.deals()
    .then(function (data) {
      var sales = data && Array.isArray(data.sales) ? data.sales : [];
      var entries = data && Array.isArray(data.products) ? data.products : [];
      var salesById = {};
      var byHandle = {};
      sales.forEach(function (sale) {
        if (sale && sale.saleId) salesById[sale.saleId] = sale;
      });
      entries.forEach(function (entry) {
        var handle = String((entry && entry.handle) || "").trim().toLowerCase();
        var sale = entry && salesById[entry.saleId];
        if (handle && sale && String(sale.mechanism || "").toLowerCase() === "automatic") byHandle[handle] = sale;
      });
      flashSalesByHandle = byHandle;
    })
    .catch(function () {
      flashSalesByHandle = {};
    });
}

function getFlashBreakdown(product, price, compareAt) {
  var HC = window.HealixColors;
  var sale = flashSalesByHandle[String((product && product.handle) || "").trim().toLowerCase()];
  if (!sale || !HC || typeof HC.flashPriceBreakdown !== "function" || !(price > 0)) return null;
  var breakdown = HC.flashPriceBreakdown({ price: price.toFixed(2), compare_at_price: compareAt > 0 ? compareAt.toFixed(2) : "" }, sale);
  return breakdown && breakdown.active && breakdown.sellingCents > breakdown.flashCents ? breakdown : null;
}

function hasRenderedCards(root) {
    if (!root) {
      return false;
    }

    return !!root.querySelector(
      ".healix-complete-look__card"
    );
  }

  function showLoading(
    root
  ) {
    if (!root) {
      return;
    }

    if (
      !root.hidden &&
      hasRenderedCards(root)
    ) {
      return;
    }

    root.hidden = false;

    root.setAttribute(
      "aria-busy",
      "true"
    );

    root.innerHTML =
      '<div class="healix-complete-look__header">' +
        '<div class="healix-complete-look__eyebrow">' +
          "Complete your look" +
        "</div>" +
      "</div>" +

      '<div class="healix-complete-look__loading">' +
        "Loading matching products..." +
      "</div>";
  }

  /* ============================================================
     RENDER
     ============================================================ */

  function render(
    root,
    categories
  ) {
    if (!root) {
      return;
    }

    var availableCategories =
      CATEGORY_ORDER.filter(
        function (category) {
          return (
            categories[category] &&
            categories[category].length
          );
        }
      );

    if (
      !availableCategories.length
    ) {
      root.hidden = true;
      root.innerHTML = "";
      root.removeAttribute(
        "aria-busy"
      );
      return;
    }

    root.hidden = false;

    root.setAttribute(
      "aria-busy",
      "false"
    );

    var html =

      '<div class="healix-complete-look__header">' +

        '<div class="healix-complete-look__eyebrow">' +
          "Complete your look" +
        "</div>" +

      "</div>";

    /* ==========================================================
       TABS
       ========================================================== */

    html +=
      '<div class="healix-complete-look__tabs" role="tablist">';

    availableCategories.forEach(
      function (
        category,
        index
      ) {
        html +=

          '<button ' +

            'type="button" ' +

            'class="healix-complete-look__tab ' +
              (
                index === 0
                  ? "is-active"
                  : ""
              ) +
            '" ' +

            'data-healix-look-tab="' +
              escapeHtml(category) +
            '" ' +

            'role="tab" ' +

            'aria-selected="' +
              (
                index === 0
                  ? "true"
                  : "false"
              ) +
            '">' +

            escapeHtml(
              sentenceCase(
                CATEGORY_LABELS[
                  category
                ]
              )
            ) +

          "</button>";
      }
    );

    html +=
      "</div>";

    /* ==========================================================
       PANELS
       ========================================================== */

    availableCategories.forEach(
      function (
        category,
        categoryIndex
      ) {
        html +=

          '<div ' +

            'class="healix-complete-look__panel ' +
              (
                categoryIndex === 0
                  ? "is-active"
                  : ""
              ) +
            '" ' +

            'data-healix-look-panel="' +
              escapeHtml(category) +
            '" ' +

            'role="tabpanel" ' +

            (
              categoryIndex !== 0
                ? "hidden"
                : ""
            ) +

          ">";

        html +=
          '<div class="healix-complete-look__track">';

        categories[
          category
        ].forEach(
          function (
            item,
            productIndex
          ) {
            var product =
              item.product;

            var variant =
              item.variant;

            var image =
              item.image;

            var title =
              product.title || "";

            var url =
              getProductUrl(
                product
              );

            var color =
              item.color || "";

            /*
             * If size exists, don't show a misleading
             * initial price because no size has been
             * selected yet.
             */

       /*
 * NORMAL + CLEARANCE PRICING
 */
var selectedPriceVariant =
  variant || null;

if (
  item.hasSizeOption &&
  item.sizeOptions &&
  item.sizeOptions.length &&
  item.sizeOptions[0].variant
) {
  selectedPriceVariant =
    item.sizeOptions[0].variant;
}

var currentPrice = 0;
var compareAtPrice = 0;
var isClearance = false;

if (selectedPriceVariant) {
  currentPrice = Number(
    selectedPriceVariant.price || 0
  );

  compareAtPrice = Number(
    selectedPriceVariant.compare_at_price || 0
  );

  isClearance = false;
}

var flashBreakdown = getFlashBreakdown(product, currentPrice, compareAtPrice);
if (flashBreakdown) {
  isClearance = true;
  currentPrice = flashBreakdown.flashCents / 100;
  compareAtPrice = flashBreakdown.sellingCents / 100;
}

var priceHtml = "";

if (currentPrice > 0) {
  if (isClearance) {
    priceHtml =
      '<div class="healix-complete-look__price healix-complete-look__price--clearance">' +
        '<span class="healix-complete-look__sale-icon" aria-label="Clearance sale"><svg viewBox="0 0 256 256" aria-hidden="true" focusable="false"><path d="M215.79,118.17a8,8,0,0,0-5-5.66L153.18,90.9l14.66-73.33a8,8,0,0,0-13.69-7l-112,120a8,8,0,0,0,3,13l57.63,21.61L88.16,238.43a8,8,0,0,0,13.69,7l112-120A8,8,0,0,0,215.79,118.17Z"></path></svg></span>' +
        '<span class="healix-complete-look__sale-price">' +
          escapeHtml(formatCatalogMoney(currentPrice)) +
        '</span>' +
        '<s class="healix-complete-look__compare-price">' +
          escapeHtml(formatCatalogMoney(compareAtPrice)) +
        '</s>' +
      '</div>';
  } else {
    priceHtml =
      '<div class="healix-complete-look__price">' +
        escapeHtml(formatCatalogMoney(currentPrice)) +
      '</div>';
  }
}

            var loadingAttributes =
              (
                categoryIndex === 0 &&
                productIndex < 4
              )
                ? 'loading="eager" fetchpriority="high" decoding="async"'
                : 'loading="lazy" decoding="async"';

            var sizeOptionsHtml = "";

            /*
             * Size options are ONLY currently available
             * variants for this color.
             */

            if (
              item.hasSizeOption &&
              item.sizeOptions.length
            ) {
              item.sizeOptions.forEach(
                function (
                  sizeOption
                ) {
                  var sizeVariant =
                    sizeOption.variant;

                  sizeOptionsHtml +=

                    '<option ' +

                      'value="' +
                        escapeHtml(
                          sizeVariant.id
                        ) +
                      '" ' +

                      'data-price="' +
                        escapeHtml(
                          sizeVariant.price
                        ) +
                      '">' +

                      escapeHtml(
                        sizeOption.value
                      ) +

                    "</option>";
                }
              );
            }

            /*
             * Color badge is useful when several
             * cart colors are present.
             */

            var colorLabel =
              sentenceCase(
                color
              );

            html +=

              '<article ' +

                'class="healix-complete-look__card" ' +

                'data-healix-product-card ' +

                'data-product-id="' +
                  escapeHtml(
                    product.id || ""
                  ) +
                '" ' +

                'data-color="' +
                  escapeHtml(color) +
                '">' +

                /* IMAGE */

                '<a ' +

                  'class="healix-complete-look__image-link" ' +

                  'href="' +
                    escapeHtml(url) +
                  '">' +

                  (
                    image
                      ?

                      '<img ' +

                        'class="healix-complete-look__image" ' +

                        'src="' +
                          escapeHtml(
                            getHighResImageUrl(
                              image,
                              1200
                            )
                          ) +
                        '" ' +

                        'srcset="' +
                          escapeHtml(
                            getImageSrcSet(
                              image
                            )
                          ) +
                        '" ' +

                        'sizes="(min-width: 750px) 110px, 100px" ' +

                        'alt="' +
                          escapeHtml(
                            title
                          ) +
                        '" ' +

                        loadingAttributes +

                      '>'

                      :

                      '<div class="healix-complete-look__image healix-complete-look__image--empty"></div>'
                  ) +

                "</a>" +

                /*
                 * COLOR INDICATOR
                 */

                '<div class="healix-complete-look__color" title="' +
                  escapeHtml(
                    colorLabel
                  ) +
                '">' +
                  escapeHtml(
                    colorLabel
                  ) +
                "</div>" +

                /*
                 * PLUS BUTTON
                 */

                '<button ' +

                  'type="button" ' +

                  'class="healix-complete-look__add" ' +

                  'data-healix-look-add ' +

                  'data-product-id="' +
                    escapeHtml(
                      product.id || ""
                    ) +
                  '" ' +

                  'data-color="' +
                    escapeHtml(color) +
                  '" ' +

                  'aria-label="Add ' +
                    escapeHtml(title) +
                    ' in ' +
                    escapeHtml(colorLabel) +
                    ' to cart">' +

                  "+" +

                "</button>" +

                /*
                 * PRODUCT INFO
                 */

                '<div class="healix-complete-look__info">' +

                  '<a ' +

                    'class="healix-complete-look__name" ' +

                    'href="' +
                      escapeHtml(url) +
                    '">' +

                    escapeHtml(title) +

                  "</a>" +

                 priceHtml +

                "</div>" +

              "</article>";
          }
        );

        html +=
          "</div>";

        html +=
          "</div>";
      }
    );

    root.innerHTML =
      html;

    bindRenderedEvents(root);
  }

  /* ============================================================
     FIND CARD
     ============================================================ */

  function findParentCard(
    element
  ) {
    var node = element;

    while (
      node &&
      node !== document.body
    ) {
      if (
        node.nodeType === 1 &&
        node.hasAttribute &&
        node.hasAttribute(
          "data-healix-product-card"
        )
      ) {
        return node;
      }

      node =
        node.parentNode;
    }

    return null;
  }

  /* ============================================================
     SIZE POPUP
     ============================================================ */


  /* ============================================================
     RENDER EVENTS
     ============================================================ */

  function bindRenderedEvents(
    root
  ) {
    /* TABS */

    var tabs =
      root.querySelectorAll(
        "[data-healix-look-tab]"
      );

    Array.prototype.forEach.call(
      tabs,
      function (tab) {
        tab.addEventListener(
          "click",
          function () {
            var target =
              tab.getAttribute(
                "data-healix-look-tab"
              );

            Array.prototype.forEach.call(
              tabs,
              function (
                otherTab
              ) {
                var active =
                  otherTab === tab;

                otherTab.classList.toggle(
                  "is-active",
                  active
                );

                otherTab.setAttribute(
                  "aria-selected",
                  active
                    ? "true"
                    : "false"
                );
              }
            );

            var panels =
              root.querySelectorAll(
                "[data-healix-look-panel]"
              );

            Array.prototype.forEach.call(
              panels,
              function (panel) {
                var active =
                  panel.getAttribute(
                    "data-healix-look-panel"
                  ) === target;

                panel.classList.toggle(
                  "is-active",
                  active
                );

                panel.hidden =
                  !active;
              }
            );
          }
        );
      }
    );

    /* ADD / PLUS BUTTONS */

    var buttons =
      root.querySelectorAll(
        "[data-healix-look-add]"
      );

    Array.prototype.forEach.call(
      buttons,
      function (button) {
        button.addEventListener(
          "click",
          async function (event) {
            event.preventDefault();
            event.stopPropagation();

            if (button.disabled) {
              return;
            }

            var card =
              findParentCard(
                button
              );

            if (!card) {
              return;
            }

            /*
             * Find recommendation object from
             * rendered data.
             */

            var productId =
              button.getAttribute(
                "data-product-id"
              );

            var color =
              button.getAttribute(
                "data-color"
              );

            var recommendation =
              findRecommendationFromRoot(
                root,
                productId,
                color
              );

            if (!recommendation) {
              return;
            }

            /*
             * SIZE PRODUCT:
             * show popup.
             */

           if (recommendation.hasSizeOption) {
  openCartVariantSheet(
    recommendation,
    button
  );

  return;
}

            /*
             * NO SIZE:
             * add immediately.
             */

           await addVariantToCart(
  recommendation.variant,
  button,
  recommendation.product
);
          }
        );
      }
    );
  }

  /*
   * The rendered DOM itself does not contain the full
   * recommendation object, so this helper reconstructs
   * the required variant information from the latest
   * prepared data stored on the root.
   */
function openCartVariantSheet(
  recommendation,
  button
) {
  if (
    !window.HealixVariantSheet ||
    typeof window.HealixVariantSheet.open !== "function"
  ) {
    console.warn(
      "[Healix Complete Look] HealixVariantSheet is not available."
    );

    return;
  }

  if (
    !recommendation ||
    !recommendation.product
  ) {
    return;
  }

  var product =
    recommendation.product;

  var imageUrl = null;

  if (recommendation.image) {
    imageUrl = recommendation.image;
  } else if (product.featured_image) {
    imageUrl = product.featured_image;
  } else if (
    product.images &&
    product.images.length
  ) {
    imageUrl = product.images[0];
  }

  var sheetItem = {
    handle:
      product.handle ||
      recommendation.handle ||
      "",

    title:
      product.title ||
      "",

    imageUrl:
      imageUrl,

    price:
      recommendation.variant &&
      recommendation.variant.price != null
        ? recommendation.variant.price
        : null
  };

  if (!sheetItem.handle) {
    console.warn(
      "[Healix Complete Look] Cannot open variant sheet: product handle missing.",
      recommendation
    );

    return;
  }

  window.HealixVariantSheet
    .open({
      items: [sheetItem],
      heading: sheetItem.title
    })
    .catch(function(error) {
      console.warn(
        "[Healix Complete Look] Variant sheet dismissed/failed.",
        error
      );
    });
}
  function findRecommendationFromRoot(
    root,
    productId,
    color
  ) {
    var recommendations =
      root._healixRecommendations || [];

    for (
      var i = 0;
      i < recommendations.length;
      i++
    ) {
      var item =
        recommendations[i];

      if (
        String(
          item.product.id
        ) ===
        String(productId) &&
        canonicalColor(
          item.color
        ) ===
        canonicalColor(color)
      ) {
        return item;
      }
    }

    return null;
  }

  /* ============================================================
     ADD VARIANT TO CART
     ============================================================ */

 async function addVariantToCart(
  variant,
  button,
  product
) {
    if (
      !variant ||
      !variant.id
    ) {
      return;
    }

    if (
      variant.available !== true
    ) {
      return;
    }

    if (
      button &&
      button.disabled
    ) {
      return;
    }

    var oldText =
      button
        ? button.innerHTML
        : "+";

    if (button) {
      button.disabled = true;
      button.innerHTML = "…";
    }

    try {
      var response =
        await fetch(
          shopifyRoot() +
            "cart/add.js",
          {
            method: "POST",

            credentials:
              "same-origin",

            headers: {
              "Content-Type":
                "application/json",

              Accept:
                "application/json"
            },

            body:
              JSON.stringify({
                items: [
                  {
                    id:
                      Number(
                        variant.id
                      ),

                    quantity: 1
                  }
                ]
              })
          }
        );

      if (!response.ok) {
        throw new Error(
          "Cart add failed"
        );
      }

      
var cartResponse = await response.json();

/*
 * Track only after Shopify confirms the add succeeded
 * and the response has been parsed successfully.
 */

      if (
        window.HealixAmplitude &&
        typeof window.HealixAmplitude.trackAddToCart ===
          "function"
      ) {
        var addToCartProperties = {
          product_id:
            product && product.id != null
              ? String(product.id)
              : "",
          product_name:
            product && product.title
              ? product.title
              : "",
          variant_id: String(variant.id),
          source: "complete_your_look"
        };

        var tracked =
          window.HealixAmplitude.trackAddToCart(
            addToCartProperties
          );

        console.log(
          "[Healix Complete Look] Add-to-cart event requested.",
          {
            properties: addToCartProperties,
            acceptedByTrackingAPI: tracked
          }
        );
      } else {
        console.error(
          "[Healix Complete Look] Amplitude tracking API unavailable after successful cart add."
        );
      }



      /*
       * Close popup immediately after successful add.
       */

      closeSizePopup();

      try {
        document.dispatchEvent(
          new CustomEvent(
            "cart:updated",
            {
              bubbles: true
            }
          )
        );
      } catch (e) {}

      try {
        document.dispatchEvent(
          new CustomEvent(
            "cart:refresh",
            {
              bubbles: true
            }
          )
        );
      } catch (e) {}

      /*
       * Refresh recommendations.
       */

      scheduleInitialize(150);

    } catch (error) {
      console.error(
        "[Healix Complete Look]",
        error
      );

      if (button) {
        button.disabled = false;
        button.innerHTML =
          oldText;
      }
    }
  }

  /* ============================================================
     INITIALIZE
     ============================================================ */

  async function initialize() {
    var generation =
      ++renderGeneration;

    var roots =
      Array.prototype.slice.call(
        document.querySelectorAll(
          ROOT_SELECTOR
        )
      );

    if (!roots.length) {
      return;
    }

    var cart;

    try {
      cart =
        await getCart();
    } catch (error) {
      console.error(
        "[Healix Complete Look]",
        error
      );

      return;
    }

    if (
      generation !==
      renderGeneration
    ) {
      return;
    }

    /* EMPTY CART */

    if (
      !cart ||
      !Array.isArray(cart.items) ||
      !cart.items.length
    ) {
      roots.forEach(
        function (root) {
          root.hidden = true;
          root.innerHTML = "";

          root.removeAttribute(
            "data-healix-rendered-color"
          );

          root._healixRecommendations =
            [];
        }
      );

      return;
    }

    /* ==========================================================
       GET EVERY COLOR FROM EVERY CART ITEM
       ========================================================== */

    var cartColors =
      getCartColors(cart);

    if (!cartColors.length) {
      log(
        "Could not detect any cart colors."
      );

      roots.forEach(
        function (root) {
          root.hidden = true;
          root.innerHTML = "";
          root._healixRecommendations =
            [];
        }
      );

      return;
    }

    log(
      "ALL CART COLORS:",
      cartColors
    );

    /*
     * Example:
     *
     * Cart:
     * 1. Vital Navy Top
     * 2. Teal Tonic Pant
     * 3. Pulse Pink Top
     *
     * cartColors:
     *
     * [
     *   "vital navy",
     *   "teal tonic",
     *   "pulse pink"
     * ]
     */

    roots.forEach(
      function (root) {
        showLoading(root);
      }
    );

    /* ==========================================================
       LOAD COLLECTION FOR EVERY CART COLOR
       ========================================================== */

    var colorCollections = [];

    for (
      var colorIndex = 0;
      colorIndex < cartColors.length;
      colorIndex++
    ) {
      if (
        generation !==
        renderGeneration
      ) {
        return;
      }

      var color =
        cartColors[
          colorIndex
        ];

      /*
       * Find the cart item associated with this color.
       * Gender is taken from the first cart item
       * having this color.
       */

      var colorCartItem = null;

      for (
        var itemIndex = 0;
        itemIndex < cart.items.length;
        itemIndex++
      ) {
        var itemColor =
          detectColor(
            cart.items[itemIndex]
          );

        if (
          itemColor &&
          canonicalColor(
            itemColor
          ) ===
          canonicalColor(
            color
          )
        ) {
          colorCartItem =
            cart.items[itemIndex];

          break;
        }
      }

      var handles =
        getCollectionHandles(
          color,
          colorCartItem
        );

      var colorProducts = [];

      /*
       * Try the collection matching the detected
       * gender first.
       *
       * If gender is unknown, try women then men.
       */

      for (
        var handleIndex = 0;
        handleIndex < handles.length;
        handleIndex++
      ) {
        if (
          generation !==
          renderGeneration
        ) {
          return;
        }

        var loadedProducts =
          await getCollectionProducts(
            handles[
              handleIndex
            ]
          );

        if (
          loadedProducts.length
        ) {
          colorProducts =
            loadedProducts;

          log(
            "COLOR COLLECTION:",
            color,
            handles[
              handleIndex
            ],
            "PRODUCTS:",
            loadedProducts.length
          );

          break;
        }
      }

      colorCollections.push({
        color: color,
        products: colorProducts
      });
    }

    if (
      generation !==
      renderGeneration
    ) {
      return;
    }

    /* ==========================================================
       PREPARE ALL COLORS TOGETHER
       ========================================================== */

    /* Load the flash-sale feed (same source as Complete the Set) before building cards */
    await loadFlashSales();

    if (generation !== renderGeneration) {
      return;
    }

    var categories = prepareProducts(cart, colorCollections);

    if (
      generation !==
      renderGeneration
    ) {
      return;
    }

    /*
     * Re-find roots because Shopify can replace
     * the cart drawer during async requests.
     */

    roots =
      Array.prototype.slice.call(
        document.querySelectorAll(
          ROOT_SELECTOR
        )
      );

    /*
     * Build a flat recommendation list.
     *
     * This is used by the size popup.
     */

    var allRecommendations = [];

    CATEGORY_ORDER.forEach(
      function (category) {
        if (
          !categories[category]
        ) {
          return;
        }

        categories[
          category
        ].forEach(
          function (item) {
            allRecommendations.push(
              item
            );
          }
        );
      }
    );

    roots.forEach(
      function (root) {
        /*
         * Store recommendations on root
         * so clicking + can find the correct
         * color + size variants.
         */

        root._healixRecommendations =
          allRecommendations;

        render(
          root,
          categories
        );

        /*
         * render() replaces innerHTML,
         * but the custom property survives.
         */

        root._healixRecommendations =
          allRecommendations;
      }
    );

    log(
      "COMPLETE YOUR LOOK RENDERED",
      {
        cartColors:
          cartColors,

        colorCollections:
          colorCollections.map(
            function (data) {
              return {
                color:
                  data.color,
                products:
                  data.products.length
              };
            }
          ),

        pants:
          categories.pants.length,

        accessories:
          categories.accessories.length,

        jackets:
          categories.jackets.length,

        jumpsuits:
          categories.jumpsuits.length,

        underscrubs:
          categories.underscrubs.length,

        tops:
          categories.tops.length,

        currency:
          getActiveCurrencyCode()
      }
    );
  }

  /* ============================================================
     SCHEDULE INITIALIZE
     ============================================================ */

  function scheduleInitialize(
    delay
  ) {
    if (scheduleTimer) {
      clearTimeout(
        scheduleTimer
      );
    }

    scheduleTimer =
      setTimeout(
        function () {
          scheduleTimer = null;

          initialize();
        },
        typeof delay === "number"
          ? delay
          : 0
      );
  }

  /* ============================================================
     CART EVENTS
     ============================================================ */

  function bindEvents() {
    [
      "cart:updated",
      "cart:refresh",
      "cart:change",
      "cart:add"
    ].forEach(
      function (eventName) {
        document.addEventListener(
          eventName,
          function () {
            scheduleInitialize(0);
          }
        );
      }
    );

    document.addEventListener(
      "shopify:section:load",
      function () {
        scheduleInitialize(0);
      }
    );

    /*
     * ESC closes size popup.
     */

    document.addEventListener(
      "keydown",
      function (event) {
        if (
          event.key === "Escape"
        ) {
          closeSizePopup();
        }
      }
    );
  }

  /* ============================================================
     MUTATION OBSERVER
     ============================================================ */

  function bindMutationObserver() {
    if (
      !window.MutationObserver
    ) {
      return;
    }

    var observer =
      new MutationObserver(
        function (mutations) {
          var found = false;

          for (
            var i = 0;
            i < mutations.length;
            i++
          ) {
            var mutation =
              mutations[i];

            if (
              !mutation.addedNodes ||
              !mutation.addedNodes.length
            ) {
              continue;
            }

            for (
              var j = 0;
              j < mutation.addedNodes.length;
              j++
            ) {
              var node =
                mutation.addedNodes[j];

              if (
                !node ||
                node.nodeType !== 1
              ) {
                continue;
              }

              if (
                node.matches &&
                node.matches(
                  ROOT_SELECTOR
                )
              ) {
                found = true;
                break;
              }

              if (
                node.querySelector &&
                node.querySelector(
                  ROOT_SELECTOR
                )
              ) {
                found = true;
                break;
              }
            }

            if (found) {
              break;
            }
          }

          if (found) {
            scheduleInitialize(0);
          }
        }
      );

    observer.observe(
      document.body,
      {
        childList: true,
        subtree: true
      }
    );
  }

  /* ============================================================
     CSS
     ============================================================ */

function injectTypographyCSS() {
  if (
    document.getElementById(
      "healix-complete-look-typography"
    )
  ) {
    return;
  }

  var style = document.createElement("style");

  style.id =
    "healix-complete-look-typography";

  style.textContent =

    /* =========================================================
       HEALIX COMPLETE YOUR LOOK
       ONLY TYPOGRAPHY
       
       IMPORTANT:
       - Do NOT style the popup here.
       - Do NOT globally style every child.
       - Keep this component scoped.
    ========================================================= */

    '[data-healix-complete-look] .healix-complete-look__header .healix-complete-look__title,' +
    '[data-healix-complete-look] .healix-complete-look__title {' +

      'display: block !important;' +

      'margin: 0 !important;' +
      'padding: 0 !important;' +

      'width: 100% !important;' +

      'font-family: inherit !important;' +
      'font-size: 18px !important;' +
      'font-weight: 500 !important;' +
      'line-height: 1.25 !important;' +

      'letter-spacing: 0 !important;' +
      'text-transform: none !important;' +
      'font-style: normal !important;' +
      'text-decoration: none !important;' +

      'color: #292d35 !important;' +

      'text-align: left !important;' +

      'text-shadow: none !important;' +

      'background: transparent !important;' +
      'border: 0 !important;' +
      'box-shadow: none !important;' +

    '}' +


    /* =========================================================
       EYEBROW
       
       Keep this separate from the main title.
    ========================================================= */

    '[data-healix-complete-look] .healix-complete-look__eyebrow {' +

      'margin: 0px 1px 13px 2px !important;' +

      'font-family: inherit !important;' +
      'font-size: 18px !important;' +
      'font-weight: 300 !important;' +
      'line-height: 1.2 !important;' +

      'letter-spacing: 0 !important;' +
      'text-transform: none !important;' +

      'color: #1d2129 !important;' +

    '}' +


    /* =========================================================
       TABS
    ========================================================= */

    '[data-healix-complete-look] .healix-complete-look__tab {' +

      'font-family: inherit !important;' +
      'text-transform: none !important;' +
      'letter-spacing: 0 !important;' +

    '}' +


    /* =========================================================
       PRODUCT NAME
    ========================================================= */

    '[data-healix-complete-look] .healix-complete-look__name {' +

      'font-family: inherit !important;' +
      'text-transform: none !important;' +
      'letter-spacing: 0 !important;' +

    '}' +


    /* =========================================================
       PRODUCT PRICE
    ========================================================= */

    '[data-healix-complete-look] .healix-complete-look__price {' +

      'font-family: inherit !important;' +
      'text-transform: none !important;' +
      'letter-spacing: 0 !important;' +

    '}' +


    /* =========================================================
       COLOR
    ========================================================= */

    '[data-healix-complete-look] .healix-complete-look__color {' +

      'font-family: inherit !important;' +
      'font-size: 11px !important;' +
      'line-height: 1.2 !important;' +
      'font-weight: 400 !important;' +

      'margin-top: 3px !important;' +

      'color: #737986 !important;' +

      'text-transform: none !important;' +
      'letter-spacing: 0 !important;' +

      'white-space: nowrap;' +
      'overflow: hidden;' +
      'text-overflow: ellipsis;' +

    '}' +


    /* =========================================================
       IMAGES

       Keep image behavior only.
       No popup styles here.
    ========================================================= */

    '[data-healix-complete-look] .healix-complete-look__image {' +

      'display: block;' +

      'width: 100% !important;' +
      'height: 100% !important;' +

      'object-fit: cover !important;' +
      'object-position: center center;' +

      'image-rendering: auto;' +

      'filter: none !important;' +
      'transform: none !important;' +

      'backface-visibility: hidden;' +
      '-webkit-backface-visibility: hidden;' +

    '}';
        +

    '[data-healix-complete-look] .healix-complete-look__price--clearance {' +
      'display: flex !important;' +
      'align-items: center !important;' +
      'gap: 5px !important;' +
      'flex-wrap: wrap !important;' +
    '}' +

    '[data-healix-complete-look] .healix-complete-look__sale-icon {' +
      'color: #f5b400 !important;' +
      'font-size: 17px !important;' +
      'line-height: 1 !important;' +
    '}' +

    '[data-healix-complete-look] .healix-complete-look__sale-price {' +
      'color: #e33b2f !important;' +
      'font-weight: 500 !important;' +
    '}' +

    '[data-healix-complete-look] .healix-complete-look__compare-price {' +
      'color: #aeb4bd !important;' +
      'text-decoration: line-through !important;' +
      'font-size: 0.9em !important;' +
    '}';


  document.head.appendChild(style);
}
  /* ============================================================
     START
     ============================================================ */

  function init() {
    if (initialized) {
      return;
    }

    initialized = true;

    injectTypographyCSS();

    bindEvents();

    bindMutationObserver();

    scheduleInitialize(0);

    log(
      "HEALIX COMPLETE YOUR LOOK ACTIVE"
    );
  }

  if (
    document.readyState ===
    "loading"
  ) {
    document.addEventListener(
      "DOMContentLoaded",
      init,
      {
        once: true
      }
    );
  } else {
    init();
  }

})();
